import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { Dropdown, DropdownToggle, DropdownMenu } from 'reactstrap';

import BootstrapIcon from '@/page-components/BootstrapIcon/BootstrapIcon';
import Modal from '@/page-components/DialogButton/lib/Modal';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  defaultBillingPrefs,
  fetchUserPreferences,
  PREFERENCE_KEYS,
  pickShape,
  saveUserPreferences,
  selectBillingPrefs,
  selectNShowcase,
} from '@/store/slices/userPreferences';
import { userBrandsList, selectSelectedCasinoCurrency } from '@/store/slices/casino-instance';
import {
  cancelInvoice,
  clearWithdrawalSubmitError,
  createWithdrawalRequest,
  fetchInvoices,
  fetchOwnerWallets,
  fetchPendingWithdrawalRequests,
  fetchResolvedWithdrawalRequests,
  fetchWalletOperations,
  fetchWalletTransactions,
  Invoice,
  InvoicePaymentEntry,
  RawWallet,
  resolveMainWalletForCurrency,
  WalletOperation,
  WalletTransaction,
  WithdrawalRequest,
} from '@/store/slices/wallet';
import type { Brand, BrandDetails } from '@/components/modules/brands-selector';

import { Button, PageShell, Skeleton, Text, Toast } from '../../components';
import { ACQ } from '../../theme/tokens';
import { useToast } from '../../hooks/useToast';
import { cleanAmountInput, currencySymbol, formatAmountDisplay, formatAmountWithCode } from '../../utils/money';
import { useMockEndpoint } from '../../hooks/useMockEndpoint';
import { Sheet } from '@/nova/components/Sheet';
import { DepositModal } from './components/DepositModal';
import { CancelInvoiceModal } from './components/CancelInvoiceModal';
// Reuse the SAME tab strip the campaign + promotion tables use (Promotions imports it from
// there too) instead of a bespoke control: the page-level strip out of `CampaignBrowser.styles`,
// with the count badge still coming from the atom file beside it.
import { TabCount } from '../acquisition/components/Campaigns/Campaigns.styles';
import { BrowserTab, BrowserTabScroll, TabsBar } from '../acquisition/components/Campaigns/CampaignBrowser.styles';
import { ROUTE } from '../../routes';

import { availablePayoutMethods, PayoutMethodKind, resolveActivePayout } from './payoutMethods';
import { paymentRowValue } from './invoice-payment-label';

import './wallet.scss';

const DEBUG = false; // set to true to enable extra logging for development

/* ---------- types ---------- */

type MonthlySettlement = {
  id: string;
  start_date: number;
  end_date: number;
  currency: string;
  total_player_waggers: number;
  total_player_wins: number;
  gross_revenue: number;
  operating_fees: number;
  payment_processing_fees: number;
  gamification_fees: number;
  total_deductions: number;
  net_revenue: number;
  description: string;
};

/* settlement breakdown dialog (node Ufx4wOhvG8) — Figma 6525:55898 */

/* one REVENUE line (Player Turnover / Return to players). `amount` is signed:
   negative renders with a leading − in the warning colour. */
type SettlementRevenueLine = {
  marker: string; // category glyph rendered before the title (▪ / ↩)
  title: string;
  sub: string;
  amount: number;
};

/* one row of an expandable deduction's breakdown (e.g. "Card payments …") */
type SettlementSubItem = {
  label: string;
  amount: number; // negative
};

/* one DEDUCTIONS FROM NET REVENUE line; `items` (when present) is revealed by the
   See more / See less toggle, `defaultExpanded` mirrors the Figma frame's state. */
type SettlementDeduction = {
  id: string;
  marker: string; // category glyph (+ / - / ○)
  title: string;
  sub: string;
  amount: number; // negative
  items?: SettlementSubItem[];
  defaultExpanded?: boolean;
};

type SettlementBreakdown = {
  periodLabel: string; // "March 2026 · Settled"
  monthLabel: string; // "March 2026"
  currency: string;
  revenueLines: SettlementRevenueLine[];
  grossRevenue: number;
  grossRevenueSub: string;
  deductions: SettlementDeduction[];
  totalDeductions: number;
  netRevenue: number;
};

type WalletAccount = {
  id: string;
  name: string;
  balance: number;
  currency: string;
};

type ActivityType = 'deposit' | 'withdrawal' | 'montly_settlement' | string;

type ActivityData = {
  date: number;
  description: string;
  amount: number;
  currency: string;
  funds_source?: string;
};

type Activity = {
  type: ActivityType;
  data: ActivityData;
};

type WalletProps = {
  styleText?: string;
  className?: string;
};

/* ---------- hardcoded data (was provided through DataElementContext) ---------- */

const hardcodedMonthlySettlements: MonthlySettlement[] = [
  {
    id: '1',
    start_date: 1693526400,
    end_date: 1696118400,
    currency: 'USD',
    total_player_waggers: 12341,
    total_player_wins: 32321312,
    gross_revenue: 12313,
    operating_fees: 1231,
    payment_processing_fees: 123,
    gamification_fees: 44,
    total_deductions: 650,
    net_revenue: 1234,
    description: 'Monthly Settlement for June 2026',
  },
  {
    id: '2',
    start_date: 1683526400,
    end_date: 1686118400,
    currency: 'USD',
    total_player_waggers: 5341,
    total_player_wins: 3215,
    gross_revenue: 1232,
    operating_fees: 1231,
    payment_processing_fees: 3213,
    gamification_fees: 123,
    total_deductions: 522,
    net_revenue: 9836,
    description: 'Monthly Settlement for May 2026',
  },
];

/* TEMP mock for the settlement breakdown dialog (node Ufx4wOhvG8).
   Replace this object with the settlement-breakdown API response once it exists —
   the dialog reads only `breakdownEp.data`, so the swap is local to <Wallet>. */
const mockSettlementBreakdown: SettlementBreakdown = {
  periodLabel: 'March 2026 · Settled',
  monthLabel: 'March 2026',
  currency: 'USD',
  revenueLines: [
    { marker: '▪', title: 'Player Turnover (Stakes)', sub: 'Money wagered by players', amount: 50000 },
    { marker: '↩', title: 'Return to players (Wins)', sub: 'Player wallets and payouts', amount: -47500 },
  ],
  grossRevenue: 2500,
  grossRevenueSub: 'Earnings after player winnings',
  deductions: [
    {
      id: 'operating_fee',
      marker: '+',
      title: 'Operating fee (30%)',
      sub: 'Managed casino operations & platform features',
      amount: -500,
      defaultExpanded: false,
      items: [
        { label: 'Platform & operations', amount: -350 },
        { label: 'Compliance & support', amount: -150 },
      ],
    },
    {
      id: 'payment_processing',
      marker: '-',
      title: 'Payment processing fees',
      sub: 'Fees towards payment processors by Total Deposits',
      amount: -370,
      defaultExpanded: true,
      items: [
        { label: 'Card payments (6% fee of $5000 processed)', amount: -300 },
        { label: 'Crypto (0% fee of $0 processed)', amount: 0 },
        { label: 'Spei payments (7% fee of $1000 processed)', amount: -70 },
      ],
    },
    {
      id: 'gamification',
      marker: '○',
      title: 'Gamification fees',
      sub: 'Manage in Product',
      amount: -150,
      defaultExpanded: true,
      items: [
        { label: 'Tournaments', amount: -70 },
        { label: 'Calendar', amount: -50 },
        { label: 'Challenges', amount: -30 },
      ],
    },
  ],
  totalDeductions: 1020,
  netRevenue: 1480,
};

/* ---------- helpers ---------- */

/* Coerce a possibly-NaN/undefined value to a finite number, so a malformed amount
   from the API renders as $0 rather than "$NaN". */
const finite = (n: number): number => {
  const v = Number(n ?? 0);
  return Number.isFinite(v) ? v : 0;
};

const money = (n: number): string => `$${finite(n).toLocaleString('en-US')}`;
const negMoney = (n: number): string => `-$${finite(n).toLocaleString('en-US')}`;

/* AI-credit movements are denominated in AIC (the AI_CREDITS wallet's currency). They read as a bare
   count beside the Lumio credit mark (never a `$`), and live in their own history tab. */
// The real Lumio OUTLINE spark (same asset the header credits chip + Buy-credits modal use).
const LUMIO_SPARK_SRC = '/assets/acq/lumio-spark.svg';
const AI_CREDIT_CURRENCY = 'AIC';
const isAiCreditWallet = (type: string | undefined): boolean => type === 'AI_CREDITS';
const creditsAmount = (n: number): string => finite(n).toLocaleString('en-US');

/* Amount-input formatting (raw while focused, grouped when blurred) is shared with the
   ad-budget top-up field so both behave identically. See utils/money.ts. */

/* signed money for the breakdown dialog — uses the real minus sign (U+2212) and
   a leading $ on both sides, matching the Figma ("−$47,500" / "$50,000"). */
const signedMoney = (n: number): string => {
  const v = finite(n);
  const abs = `$${Math.abs(v).toLocaleString('en-US')}`;
  return v < 0 ? `-${abs}` : abs;
};

/* node `he2JPd68DQ` Layout propFunction: pick the activity dot colour from type / funds_source */
const activityDotClass = (a: Activity): string => {
  switch (a.type) {
    case 'montly_settlement':
      return 'dot-green';
    case 'deposit':
      if (a.data?.funds_source === 'balance') return 'dot-blue';
      if (a.data?.funds_source === 'personal_funds') return 'dot-yellow';
      return '';
    case 'withdrawal':
    case 'withdraw':
      return 'dot-orange';
    default:
      return '';
  }
};

/* ---------- activity feed (operations + transactions → the activity list rows) ---------- */

/* Everything the details modal shows for one activity row — resolved up-front so the
   modal is a pure render of already-derived values. */
type ActivityDetail = {
  title: string;
  amount: string; // pre-formatted, signed
  amountClass: string;
  dotClass: string;
  typeLabel: string; // human operation type (Top-up / Deposit / …)
  statusLabel?: string; // Completed / Failed / Pending
  statusKind?: 'ok' | 'warn' | 'pending';
  brandName?: string; // the casino this movement belongs to
  brandLogo?: string | null;
  fromLabel?: string; // source wallet (friendly)
  toLabel?: string; // destination wallet (friendly), omitted for same-wallet moves
  dateFull: string; // "18 Jun 2026, 09:10"
  currency: string;
  balanceAfter?: string; // destination wallet balance after the movement
  reference: string; // operation id
  reason?: string; // back-office note (e.g. a withdrawal rejection reason)
  invoiceId?: string; // a linked platform invoice, when the movement references one
  aiCredits?: boolean; // denominated in AI credits → Lumio mark instead of $
};

type ActivityRow = {
  key: string;
  dotClass: string; // colours the .layout-wrp wrapper → .activity-dot
  amountClass: string; // amount-green / amount-orange / amount-muted
  title: string;
  sub: string;
  amount: string; // pre-formatted, signed
  ts: number; // for sorting, newest first
  dateShort: string; // "Jun 18" — shown under the amount
  brandName?: string; // the casino whose wallet was topped up
  brandLogo?: string | null;
  invoiceId?: string; // a linked platform invoice, when the movement references one
  aiCredits?: boolean; // denominated in AI credits → Lumio mark + own tab (no $)
  detail: ActivityDetail; // everything the click-through modal renders
};

/* A money movement can reference the platform invoice it settled. The wallet ledger doesn't
   carry that link today (top-ups / withdrawals aren't invoiced), so this reads defensively
   from the operation/transaction `details` and stays dormant until the backend emits one of
   these keys — at which point the activity row + details modal surface a "View invoice" link. */
const findInvoiceRef = (details: Record<string, unknown> | null | undefined): string | undefined => {
  if (!details) return undefined;
  const candidate = details.invoice_id ?? details.invoiceId ?? details.invoice;
  return typeof candidate === 'string' && candidate ? candidate : undefined;
};

const humanize = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);

/* Friendly names for the wallet types the activity rows reference. Each known wallet
   type maps to an explicit translation literal so the copy is both harvested by the
   extractor and re-translated reactively at the render-time call (the type stays a
   stable English id). */
const walletLabel = (t: TFunction, type: string): string => {
  switch (type) {
    case 'MAIN':
      return t('Main wallet');
    case 'ACQUISITION':
      return t('Ad budget');
    case 'DAILY_ACQUISITION':
      return t('Daily ad budget');
    case 'AI_CREDITS':
      return t('AI Credits');
    default:
      return humanize(type);
  }
};

/* Friendly names for the raw operation types (the `details` flags drive the title;
   this is the coarser movement category shown in the details modal). Explicit literals,
   same reasoning as walletLabel above. */
const opTypeLabel = (t: TFunction, type: string | undefined): string => {
  switch ((type ?? '').toUpperCase()) {
    case 'TOPUP':
      return t('Budget top-up');
    case 'DEPOSIT':
      return t('Deposit');
    case 'WITHDRAWAL':
      return t('Payout');
    case 'TRANSFER':
      return t('Transfer');
    case 'SPEND_AI_CREDITS':
      return t('AI credits used');
    case 'REFUND_AI_CREDITS':
      return t('Refund');
    default:
      return humanize((type ?? '').replace(/_/g, ' '));
  }
};

const statusInfo = (
  t: TFunction,
  status: string | undefined,
): { label: string; kind: 'ok' | 'warn' | 'pending' } | undefined => {
  const s = (status ?? '').toLowerCase();
  // Settled / paid-out outcomes (green).
  if (s === 'completed') return { label: t('Completed'), kind: 'ok' };
  if (s === 'approved') return { label: t('Approved'), kind: 'ok' };
  if (s === 'paid') return { label: t('Paid'), kind: 'ok' };
  if (s === 'processed') return { label: t('Processed'), kind: 'ok' };
  // Refused / reverted outcomes (warning).
  if (s === 'failed') return { label: t('Failed'), kind: 'warn' };
  if (s === 'rejected') return { label: t('Rejected'), kind: 'warn' };
  if (s === 'cancelled' || s === 'canceled') return { label: t('Cancelled'), kind: 'warn' };
  if (s === 'declined') return { label: t('Declined'), kind: 'warn' };
  // Still in flight (neutral / amber).
  if (s === 'pending') return { label: t('Pending'), kind: 'pending' };
  if (s === 'processing') return { label: t('Processing'), kind: 'pending' };
  return s ? { label: humanize(s), kind: 'pending' } : undefined;
};

/* The owner-level (MAIN) wallet carries an all-zeros casino instance sentinel; a
   real brand never does. Treat missing / all-zeros-and-dashes ids as "not a brand". */
const isOwnerInstance = (id: string | null | undefined): boolean => !id || /^[0-]+$/.test(id);

/* The brand's logo source, hardened against the two shapes the casino-instance API
   ships: the per-slot variant map now lives at `status_details.logoAssets.logos` (with
   `casino_data.details.logo` reduced to the single `original` URL string), while older
   records still carry that map inline at `casino_data.details.logo`. Prefer the
   `logoAssets` map, then fall back to `details.logo`. Mirrors the top-bar BrandSelector
   and the casino-settings logo resolver. */
const brandLogoSource = (brand: Brand | undefined): BrandDetails['logo'] | undefined => {
  const variantMap = brand?.status_details?.logoAssets?.logos;
  if (variantMap && Object.keys(variantMap).length > 0) {
    return variantMap;
  }
  return brand?.casino_data?.details?.logo;
};

/* `logo` is either a legacy CDN URL string or a per-slot map of { src, alt } assets;
   flatten it to a single small-format URL (mirrors the top-bar BrandSelector). */
const BRAND_LOGO_SLOTS = ['symbol', 'topNavDesktop', 'topNavMobile', 'footer', 'short_name'] as const;
const brandLogoUrl = (brand: Brand | undefined): string | null => {
  const logo = brandLogoSource(brand);
  if (!logo) return null;
  if (typeof logo === 'string') return logo;
  for (const slot of BRAND_LOGO_SLOTS) {
    const src = logo[slot]?.src;
    if (src) return src;
  }
  return Object.values(logo).find((variant) => variant?.src)?.src ?? null;
};
const brandDisplayName = (brand: Brand | undefined): string => brand?.casino_data?.details?.brand ?? '';

/* "Jun 18, 2026" — the short date shown under each row's amount (year always shown). */
const formatDateShort = (ts: number): string => {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/* "18 Jun 2026, 09:10" — the full timestamp for the details modal. */
const formatDateFull = (ts: number): string => {
  if (!ts) return '';
  return new Date(ts).toLocaleString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/* A movement's user-facing title, from the `details` flags the backend stamps on a top-up
   / ad-spend invoice (or a free-text description), falling back to the supplied default.
   The vocabulary mirrors the wallet labels above (Ad budget / Daily ad budget) so a row's
   title and its From → To flow always read in the same terms. */
const movementTitle = (t: TFunction, details: Record<string, unknown> | null | undefined, fallback: string): string => {
  if (details?.acquisition_budget_topup) return t('Ad budget top-up');
  if (details?.daily_acquisition_budget_topup) return t('Daily ad budget top-up');
  if (details?.ad_spend || details?.ad_invoice || details?.invoice) return t('Ad spend');
  if (typeof details?.description === 'string') return details.description;
  return fallback;
};

/* The four shapes a wallet movement can take, and how each is colour-coded. The owner's
   money funnels down their own wallets (Main wallet → Ad budget → Daily ad budget), and ad
   spend drains the daily wallet (which is why it can go negative). Colour tracks the owner's
   money: green for cash the owner gains or is paid out (a `deposit` credit IN, +; a
   successful `withdrawal` payout, signed − since the balance still drops), red for money
   spent on platform costs (an ad `spend` / invoice OUT, −), and a neutral hollow bullet for
   a `topup`, which only shifts funds DOWN the owner's own funnel so the total is unchanged
   (unsigned). */
type MovementKind = 'deposit' | 'topup' | 'spend' | 'withdrawal';

const KIND_STYLE: Record<MovementKind, { dotClass: string; amountClass: string; sign: '' | '+' | '-' }> = {
  deposit: { dotClass: 'dot-green', amountClass: 'amount-green', sign: '+' },
  topup: { dotClass: 'dot-outline', amountClass: 'amount-muted', sign: '' },
  spend: { dotClass: 'dot-red', amountClass: 'amount-red', sign: '-' },
  withdrawal: { dotClass: 'dot-green', amountClass: 'amount-green', sign: '-' },
};

/* Which kind an operation is. Deposits / withdrawals are tagged by `operation_type`; an ad
   spend is an explicit invoice flag OR money LEAVING a brand's daily ad budget to the
   platform (a different user — the daily wallet's only real outflow). Everything else is an
   internal top-up. The ad-spend branch is forward-modelled and lies dormant until the
   backend emits these movements, exactly like the top-up `details` flags above. */
const classifyOperation = (op: WalletOperation): MovementKind => {
  const type = (op.operation_type ?? '').toUpperCase();
  if (type === 'DEPOSIT') return 'deposit';
  if (type === 'WITHDRAWAL') return 'withdrawal';
  // A reversal returns money (its details carry the reversed op's id), so it reads as a credit back in
  // (+, green) — matching the "Refund" title — whatever its raw operation_type. Checked before the
  // spend branches so a refund is never coloured like a spend.
  if (isRefund(op.details)) return 'deposit';
  // AI-credit consumption is money OUT of the credit wallet (−, red); a refund returns it (+, green).
  if (type === 'SPEND_AI_CREDITS') return 'spend';
  if (type === 'REFUND_AI_CREDITS') return 'deposit';
  if (op.details?.ad_spend || op.details?.ad_invoice || op.details?.invoice) return 'spend';
  if (op.source_wallet_type === 'DAILY_ACQUISITION' && op.destination_user_id !== op.source_user_id) return 'spend';
  return 'topup';
};

/* The internal leg that shifts money from MAIN into the platform's invoice-holding wallet when an
   invoice is paid: redundant with the invoice record itself (and the AI-credit deposit it funds), so it
   is kept out of the activity feed entirely. Matched by the INVOICE_PAYMENT type or by the funding
   DIRECTION (money going INTO the holding wallet); a grant coming BACK OUT of it (e.g. into AI_CREDITS)
   is a real deposit and is never hidden. */
const isInvoicePaymentOp = (op: WalletOperation): boolean =>
  (op.operation_type ?? '').toUpperCase() === 'INVOICE_PAYMENT' ||
  op.destination_wallet_type === 'INVOICE_PAYMENTS';

/* A refund credits money back for an operation that was charged then reversed (e.g. a failed formats
   job): it carries the id/key of the operation it reverses in `details`, so it reads "Refund" rather
   than the generic "Deposit" a lone credit would otherwise fall back to. */
const isRefund = (details: Record<string, unknown> | null | undefined): boolean =>
  !!details && (details.refund_of_operation_id != null || details.refund_of_operation_key != null);

/* The From → To line under each row. Money crossing the owner boundary reads with a single
   preposition — a deposit "to Main wallet", an ad spend / payout "from Daily ad budget" —
   while an internal top-up shows the full funnel hop ("Main wallet → Ad budget"). */
const movementSub = (t: TFunction, kind: MovementKind, fromType?: string, toType?: string): string => {
  const from = fromType ? walletLabel(t, fromType) : '';
  const to = toType ? walletLabel(t, toType) : '';
  if (kind === 'deposit') return to ? t('to {{to}}', { to }) : '';
  if (kind === 'spend' || kind === 'withdrawal') return from ? t('from {{from}}', { from }) : '';
  if (!from || !to || from === to) return from || to;
  return `${from} → ${to}`;
};

/* Context the row builders need to resolve a movement to its casino + balance-after:
   the brand lookup and the transaction legs grouped by operation id. */
type FeedContext = {
  brandsById: Map<string, Brand>;
  legsByOpId: Map<string, TxGroup>;
  walletById: Map<string, RawWallet>;
};

/* Which casino a movement belongs to: the brand side of the move (a top-up funds a
   brand's ACQUISITION wallet, so the destination is the brand; the owner-level MAIN
   side is the all-zeros sentinel). Returns undefined for owner-only moves (deposits). */
const resolveBrand = (
  ctx: FeedContext,
  ...candidateIds: Array<string | null | undefined>
): Brand | undefined => {
  for (const id of candidateIds) {
    if (!isOwnerInstance(id)) {
      const brand = ctx.brandsById.get(id as string);
      if (brand) return brand;
    }
  }
  return undefined;
};

/* `/wallet/operations` row → ONE activity row per money movement. A top-up moves funds
   between the owner's own wallets, so it stays neutral (unsigned, muted); money crossing
   the owner boundary is signed — a deposit in (+), an ad spend / withdrawal out (−). */
const operationToRow = (t: TFunction, op: WalletOperation, ctx: FeedContext): ActivityRow => {
  const amount = Number(op.amount ?? 0);
  const kind = classifyOperation(op);
  const { dotClass, amountClass, sign } = KIND_STYLE[kind];
  // AI-credit movements read as a bare count beside the Lumio mark (no $), and live in their own tab.
  const aiCredits =
    (op.currency ?? '').toUpperCase() === AI_CREDIT_CURRENCY ||
    isAiCreditWallet(op.source_wallet_type) ||
    isAiCreditWallet(op.destination_wallet_type) ||
    /AI_CREDITS/i.test(op.operation_type ?? '');

  const sameWallet = op.source_wallet_type === op.destination_wallet_type;
  const fromLabel = walletLabel(t, op.source_wallet_type);
  const toLabel = walletLabel(t, op.destination_wallet_type);
  const sub = movementSub(t, kind, op.source_wallet_type, op.destination_wallet_type);
  // A From → To flow only makes sense for an internal top-up; a deposit / spend / payout
  // crosses the owner boundary, so the modal names just the one wallet it touched.
  const showFlow = kind === 'topup' && !sameWallet;

  const brand = resolveBrand(ctx, op.destination_casino_instance_id, op.source_casino_instance_id);
  const brandName = brand ? brandDisplayName(brand) : undefined;
  const brandLogo = brand ? brandLogoUrl(brand) : undefined;

  const ts = Date.parse(op.created_at) || 0;
  const signedAmount = `${sign}${aiCredits ? creditsAmount(amount) : money(amount)}`;
  // The operation type spells out AI-credit movements ("AI credits used") so the title never reads the
  // raw enum ("Spend_ai_credits"); a reversal reads "Refund" rather than the generic type.
  const opLabel = isRefund(op.details) ? t('Refund') : opTypeLabel(t, op.operation_type);
  const title = movementTitle(t, op.details, opLabel);
  const status = statusInfo(t, op.status);
  const invoiceId = findInvoiceRef(op.details);
  // Balance-after reads the leg on the owner's OWN wallet: the destination (credit) leg for money IN
  // (deposit / top-up), the source (debit) leg for money OUT (spend / withdrawal). So an AI-credit spend
  // reports the remaining credit balance, not a platform sink's.
  const balanceLeg =
    kind === 'spend' || kind === 'withdrawal'
      ? ctx.legsByOpId.get(op.id)?.debit
      : ctx.legsByOpId.get(op.id)?.credit;
  const balanceAfter = balanceLeg
    ? aiCredits
      ? creditsAmount(Number(balanceLeg.wallet_balance_after ?? 0))
      : money(Number(balanceLeg.wallet_balance_after ?? 0))
    : undefined;

  return {
    key: `op-${op.id}`,
    dotClass,
    amountClass,
    title,
    sub,
    amount: signedAmount,
    ts,
    dateShort: formatDateShort(ts),
    brandName,
    brandLogo,
    invoiceId,
    aiCredits,
    detail: {
      title,
      amount: signedAmount,
      amountClass,
      dotClass,
      typeLabel: opLabel,
      statusLabel: status?.label,
      statusKind: status?.kind,
      brandName,
      brandLogo,
      fromLabel,
      toLabel: showFlow ? toLabel : undefined,
      dateFull: formatDateFull(ts),
      currency: op.currency,
      balanceAfter,
      reference: op.id,
      invoiceId,
      aiCredits,
    },
  };
};

/* The ledger stores each operation as TWO transactions (a debit on the source wallet +
   a credit on the destination), both tagged with the same `operation_id`. We only fall
   back to transactions for movements with no operation in the feed (e.g. a deposit), and
   collapse their legs into one row: a lone credit is incoming (+), a lone debit outgoing,
   both legs present means an internal transfer (neutral). */
type TxGroup = { credit?: WalletTransaction; debit?: WalletTransaction };
const transactionGroupToRow = (t: TFunction, group: TxGroup, ctx: FeedContext): ActivityRow => {
  const incoming = !!group.credit && !group.debit;
  const outgoing = !!group.debit && !group.credit;
  const primary = (group.credit ?? group.debit) as WalletTransaction;

  // Resolve each leg's wallet (type + brand) so an operation-less movement is still named by
  // the wallets it touched, instead of falling back to a bare "Balance after".
  const fromWallet = group.debit ? ctx.walletById.get(group.debit.wallet_id) : undefined;
  const toWallet = group.credit ? ctx.walletById.get(group.credit.wallet_id) : undefined;
  const fromType = fromWallet?.wallet_type;
  const toType = toWallet?.wallet_type;

  // AI-credit movements read as a bare count beside the Lumio mark (no $), and live in their own tab.
  const aiCredits =
    (primary.currency ?? '').toUpperCase() === AI_CREDIT_CURRENCY ||
    isAiCreditWallet(fromType) ||
    isAiCreditWallet(toType);

  // A lone credit is money in (deposit); a lone debit is money out — from a daily ad budget OR the
  // AI-credit wallet that reads as a spend (an AI-credit spend can arrive as an operation-less
  // transaction, mirroring operationToRow's SPEND_AI_CREDITS handling), otherwise a payout. Both legs
  // present is an internal transfer.
  const kind: MovementKind = incoming
    ? 'deposit'
    : outgoing
      ? fromType === 'DAILY_ACQUISITION' || aiCredits
        ? 'spend'
        : 'withdrawal'
      : 'topup';
  const { dotClass, amountClass, sign } = KIND_STYLE[kind];

  const amount = Number(primary.amount ?? 0);
  const fallback = isRefund(primary.details)
    ? t('Refund')
    : incoming
      ? t('Deposit')
      : kind === 'spend'
        ? aiCredits
          ? t('AI credits used')
          : t('Ad spend')
        : outgoing
          ? t('Payout')
          : t('Transfer');
  const title = movementTitle(t, primary.details, fallback);
  const ts = Date.parse(primary.created_at) || 0;
  const signedAmount = `${sign}${aiCredits ? creditsAmount(amount) : money(amount)}`;
  const sub = movementSub(t, kind, fromType, toType);
  const showFlow = kind === 'topup' && !!fromType && !!toType && fromType !== toType;

  const brand = resolveBrand(ctx, toWallet?.casino_instance_id, fromWallet?.casino_instance_id);
  const brandName = brand ? brandDisplayName(brand) : undefined;
  const brandLogo = brand ? brandLogoUrl(brand) : undefined;
  const invoiceId = findInvoiceRef(primary.details);

  return {
    key: `tx-${primary.operation_id}`,
    dotClass,
    amountClass,
    title,
    sub,
    amount: signedAmount,
    ts,
    dateShort: formatDateShort(ts),
    brandName,
    brandLogo,
    invoiceId,
    aiCredits,
    detail: {
      title,
      amount: signedAmount,
      amountClass,
      dotClass,
      typeLabel: fallback,
      brandName,
      brandLogo,
      fromLabel: fromType ? walletLabel(t, fromType) : toType ? walletLabel(t, toType) : undefined,
      toLabel: showFlow ? walletLabel(t, toType as string) : undefined,
      dateFull: formatDateFull(ts),
      currency: primary.currency,
      balanceAfter: aiCredits
        ? creditsAmount(Number(primary.wallet_balance_after ?? 0))
        : money(Number(primary.wallet_balance_after ?? 0)),
      reference: primary.operation_id,
      invoiceId,
      aiCredits,
    },
  };
};

/* ---------- withdrawal requests (the "Pending withdrawals" card + resolved activity) ----------

A withdrawal request is the owner's payout ask. Its lifecycle lives in the
`/wallet/withdrawal-requests` API (source of truth), and the SAME movement is ALSO stamped
as a `WITHDRAWAL_*` operation (MAIN ↔ the WITHDRAWAL wallet) plus ledger legs. To avoid
showing one withdrawal twice, the activity feed suppresses every operation/transaction that
touches the WITHDRAWAL wallet and renders withdrawals only from this API (see
`allActivityRows` below). */

/** An operation is part of the withdrawal lifecycle when either leg is the WITHDRAWAL wallet
    (MAIN → WITHDRAWAL on request, WITHDRAWAL → MAIN on reject, WITHDRAWAL → out on payout). */
const isWithdrawalWalletOp = (op: WalletOperation): boolean =>
  op.source_wallet_type === 'WITHDRAWAL' || op.destination_wallet_type === 'WITHDRAWAL';

/** Resolved outcomes that returned the money rather than paying it out (net-zero for the
    owner), shown neutral/muted in the feed. Everything else resolved is a real payout. */
const REJECTED_WITHDRAWAL_STATUS = /reject|cancel|declin|fail/i;
const isRejectedWithdrawal = (status: string): boolean => REJECTED_WITHDRAWAL_STATUS.test(status);

/** The back-office rejection note, when present (`details.reason`). */
const withdrawalReason = (req: WithdrawalRequest): string | undefined => {
  const reason = (req.details as { reason?: unknown } | null)?.reason;
  return typeof reason === 'string' && reason.trim() ? reason : undefined;
};

/* A RESOLVED withdrawal request → one recent-activity row. A paid-out request (approved /
   completed / …) is a successful payout to the owner, colour-coded green (signed −$X, since
   the balance still drops); a rejected / cancelled request returned the funds to MAIN, so it
   nets to zero (unsigned) and is colour-coded a washed-out red — softer than the alarming
   spend red — with the back-office reason surfaced in the details modal. Ordered by
   `updated_at` (when it was resolved). */
const withdrawalRequestToRow = (t: TFunction, req: WithdrawalRequest): ActivityRow => {
  const amount = Number(req.amount ?? 0);
  const rejected = isRejectedWithdrawal(req.status);
  const dotClass = rejected ? 'dot-red-soft' : 'dot-green';
  const amountClass = rejected ? 'amount-red-soft' : 'amount-green';
  const signedAmount = `${rejected ? '' : '-'}${money(amount)}`;
  const ts = Date.parse(req.updated_at || req.created_at) || 0;
  const title = rejected ? t('Payout rejected') : t('Payout');
  const status = statusInfo(t, req.status);
  const reason = withdrawalReason(req);
  const sub = rejected ? reason ?? t('Funds returned to your balance') : t('To your payout method');

  return {
    key: `wd-${req.id}`,
    dotClass,
    amountClass,
    title,
    sub,
    amount: signedAmount,
    ts,
    dateShort: formatDateShort(ts),
    detail: {
      title,
      amount: signedAmount,
      amountClass,
      dotClass,
      typeLabel: t('Payout'),
      statusLabel: status?.label,
      statusKind: status?.kind,
      fromLabel: walletLabel(t, 'MAIN'),
      dateFull: formatDateFull(ts),
      currency: req.currency,
      reference: req.id,
      reason,
    },
  };
};

/* The transaction-details rows, in display order. This is the SINGLE source of truth for
   what a transaction shows, shared by the on-screen details modal AND the PDF receipt, so a
   new `ActivityDetail` field is reflected in both the moment it's added here (the PDF renders
   whatever rows it's handed and can't fall behind). `kind` flags the two rows the modal draws
   specially (a brand logo, the status pill); the PDF renders every row as plain label/value. */
type TxnDetailRow = {
  key: string;
  label: string;
  value: string;
  mono?: boolean; // monospace value (the reference id)
  kind?: 'brand' | 'status';
};

const buildTxnDetailRows = (t: TFunction, d: ActivityDetail): TxnDetailRow[] => {
  const rows: TxnDetailRow[] = [];
  if (d.brandName) rows.push({ key: 'brand', label: t('Website'), value: d.brandName, kind: 'brand' });
  rows.push({ key: 'type', label: t('Type'), value: d.typeLabel });
  if (d.statusLabel) rows.push({ key: 'status', label: t('Status'), value: d.statusLabel, kind: 'status' });
  if (d.fromLabel) {
    const isFlow = !!d.toLabel;
    rows.push({
      key: 'wallet',
      label: isFlow ? t('Transfer') : t('Wallet'),
      value: isFlow ? `${d.fromLabel} → ${d.toLabel}` : d.fromLabel,
    });
  }
  rows.push({ key: 'date', label: t('Date'), value: d.dateFull });
  if (d.reason) rows.push({ key: 'reason', label: t('Reason'), value: d.reason });
  if (d.balanceAfter) {
    rows.push({ key: 'balanceAfter', label: t('Balance after'), value: `${d.balanceAfter} ${d.currency}` });
  }
  rows.push({ key: 'reference', label: t('Reference'), value: d.reference, mono: true });
  return rows;
};

/* ---------- invoices (the Invoices tab) ---------- */

/* Friendly label for an invoice's `type`. Explicit literals so the copy is harvested and
   re-translated reactively (the type stays a stable English id). */
const invoiceTypeLabel = (t: TFunction, type: string): string => {
  switch ((type ?? '').toLowerCase()) {
    case 'casino_create':
      return t('Website creation');
    case 'deposit':
      return t('Wallet deposit');
    default:
      return humanize((type ?? '').replace(/_/g, ' '));
  }
};

/* `price` arrives as a string ("500" / "759.92"); render it as a 2-dp amount. USD keeps the
   page's familiar $ prefix; any other currency trails its 3-letter code. */
const formatInvoiceAmount = (price: string, currency: string): string => {
  const value = finite(Number(price));
  const formatted = value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency === 'USD' ? `$${formatted}` : `${formatted} ${currency}`;
};

/* The most recent payment-trail entry — drives the modal's "Payment" row. */
const latestPayment = (inv: Invoice): InvoicePaymentEntry | undefined => {
  const entries = inv.payment_data ?? [];
  if (!entries.length) return undefined;
  return entries.reduce((latest, e) => ((Date.parse(e.date) || 0) >= (Date.parse(latest.date) || 0) ? e : latest));
};

/* The first segment of the UUID, upper-cased — a compact, stable, human-facing number. */
const invoiceNumber = (id: string): string => (id.split('-')[0] || id).toUpperCase();

type InvoiceRow = {
  key: string;
  number: string; // short invoice reference ("0BDE03B3")
  title: string;
  amount: string; // pre-formatted price
  status?: { label: string; kind: 'ok' | 'warn' | 'pending' };
  ts: number; // created_at — sorts the list + drives the right-column date
  invoice: Invoice; // the raw record, for the details modal
};

const invoiceToRow = (t: TFunction, inv: Invoice): InvoiceRow => {
  const ts = Date.parse(inv.created_at) || 0;
  return {
    key: inv.id,
    number: invoiceNumber(inv.id),
    title: invoiceTypeLabel(t, inv.type),
    amount: formatInvoiceAmount(inv.price, inv.currency),
    status: statusInfo(t, inv.status),
    ts,
    invoice: inv,
  };
};

/** An invoice the owner can still cancel: a genuinely open charge. `paid`, `cancelled`
   and `failed` are terminal, so the Cancel action is hidden for those. */
const CANCELLABLE_INVOICE_STATUS = new Set(['pending', 'unpaid']);
const isCancellableInvoice = (status: string): boolean =>
  CANCELLABLE_INVOICE_STATUS.has((status ?? '').toLowerCase());

/** A cancelled (voided) invoice — terminal, and shown in its own "Cancelled" tab rather than
   piling up in Pending. Accepts both the British and American spellings the API may ship. */
const CANCELLED_INVOICE_STATUS = new Set(['cancelled', 'canceled']);
const isCancelledInvoice = (status: string): boolean =>
  CANCELLED_INVOICE_STATUS.has((status ?? '').toLowerCase());

/* Invoice list document glyph (from design). Drawn with `currentColor` only, so it themes
   automatically off the icon container's colour (light + dark). */
const InvoiceDocIcon = (): React.ReactElement => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M4 6V19C4 20.6569 5.34315 22 7 22H17C18.6569 22 20 20.6569 20 19V9C20 7.34315 18.6569 6 17 6H4ZM4 6V5"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M18 5.99999V6.74999H18.75V5.99999H18ZM15.7172 2.32611L15.6111 1.58365V1.58365L15.7172 2.32611ZM4.91959 3.86862L4.81353 3.12616H4.81353L4.91959 3.86862ZM5.07107 5.99999V6.74999H18V5.99999V5.24999H5.07107V5.99999ZM18 5.99999H18.75V4.30601H18H17.25V5.99999H18ZM15.7172 2.32611L15.6111 1.58365L4.81353 3.12616L4.91959 3.86862L5.02566 4.61108L15.8232 3.06857L15.7172 2.32611ZM4.91959 3.86862L4.81353 3.12616C3.91638 3.25432 3.25 4.02267 3.25 4.92892H4H4.75C4.75 4.76914 4.86749 4.63368 5.02566 4.61108L4.91959 3.86862ZM18 4.30601H18.75C18.75 2.6325 17.2678 1.34698 15.6111 1.58365L15.7172 2.32611L15.8232 3.06857C16.5763 2.961 17.25 3.54532 17.25 4.30601H18ZM5.07107 5.99999V5.24999C4.89375 5.24999 4.75 5.10624 4.75 4.92892H4H3.25C3.25 5.93467 4.06532 6.74999 5.07107 6.74999V5.99999Z"
      fill="currentColor"
    />
    <path d="M8 12H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M8 15.5H13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/* The invoice details rows — shared by the modal and the downloadable PDF receipt, the same
   pattern as `buildTxnDetailRows`, so the PDF can never fall behind what the modal shows. */
const buildInvoiceDetailRows = (t: TFunction, row: InvoiceRow): TxnDetailRow[] => {
  const inv = row.invoice;
  const payment = latestPayment(inv);
  const issuedTs = Date.parse(inv.created_at) || 0;
  const dueTs = Date.parse(inv.due_date) || 0;
  const rows: TxnDetailRow[] = [];
  rows.push({ key: 'number', label: t('Invoice no.'), value: `#${row.number}`, mono: true });
  rows.push({ key: 'issued', label: t('Issued'), value: formatDateFull(issuedTs) });
  if (dueTs) rows.push({ key: 'due', label: t('Due'), value: formatDateFull(dueTs) });
  const paymentValue = paymentRowValue(payment, formatDateFull);
  if (paymentValue) rows.push({ key: 'payment', label: t('Payment'), value: paymentValue });
  rows.push({ key: 'amount', label: t('Amount'), value: `${row.amount} ${inv.currency}` });
  if (inv.session_id) rows.push({ key: 'reference', label: t('Reference'), value: inv.session_id, mono: true });
  return rows;
};

/* The monthly settlement card (net revenue / platform fee / gamification / month
   balance + breakdown dialog) runs entirely on mock data — there's no settlement
   API yet. Hide it until the backend can provide real figures; flip to true to
   bring it back. */
const SHOW_MONTHLY_SETTLEMENT = false;

const Wallet = (props: WalletProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const ownerWallet = useAppSelector((state) => state.ownerWallet ?? null);

  /* brands back the activity feed's "which casino was topped up" resolution
     (each acquisition wallet belongs to a casino instance) */
  const brands = useAppSelector((state) => state.casinoInstance.brands);
  const brandsLoaded = useAppSelector((state) => state.casinoInstance.loaded);
  const brandsLoading = useAppSelector((state) => state.casinoInstance.loading);

  /* payout rails come from the owner's saved billing prefs (Settings → Billing) */
  const billing = useAppSelector(selectBillingPrefs);
  const prefsLoaded = useAppSelector((state) => state.userPreferences.loaded);
  const prefsLoading = useAppSelector((state) => state.userPreferences.loading);

  /* withdrawal requests: pending → the card below; resolved → the activity feed. */
  const pendingWithdrawals = useAppSelector((state) => state.ownerWallet?.pendingWithdrawals ?? []);
  const resolvedWithdrawals = useAppSelector((state) => state.ownerWallet?.resolvedWithdrawals ?? []);
  const pendingWithdrawalsLoaded = useAppSelector((state) => state.ownerWallet?.pendingWithdrawalsLoaded ?? false);
  const pendingWithdrawalsLoading = useAppSelector((state) => state.ownerWallet?.pendingWithdrawalsLoading ?? false);
  const withdrawalSubmitting = useAppSelector((state) => state.ownerWallet?.withdrawalSubmitting ?? false);
  const withdrawalSubmitError = useAppSelector((state) => state.ownerWallet?.withdrawalSubmitError ?? null);

  /* platform invoices → the "Invoices" tab */
  const invoices = useAppSelector((state) => state.ownerWallet?.invoices ?? []);
  const invoicesLoaded = useAppSelector((state) => state.ownerWallet?.invoicesLoaded ?? false);
  const invoicesLoading = useAppSelector((state) => state.ownerWallet?.invoicesLoading ?? false);
  /* the invoice id being cancelled (`POST /invoice/cancel` in flight) → confirm dialog busy state */
  const cancellingInvoiceId = useAppSelector((state) => state.ownerWallet?.cancellingInvoiceId ?? null);

  /* success / error confirmations (e.g. after cancelling an invoice) */
  const { show: showToast, toast, dismiss: dismissToast } = useToast();

  /* ---------- state ---------- */
  const [selectedSettlement, setSelectedSettlement] = React.useState<MonthlySettlement>(hardcodedMonthlySettlements[0]);
  const [openSettlementDialog, setOpenSettlementDialog] = React.useState(false);

  /* the key of the activity row whose details modal is open (null = closed). We
     store the key, not the row snapshot, so the modal always reflects the latest
     feed data (and closes cleanly if the row disappears on a refresh). */
  const [selectedActivityKey, setSelectedActivityKey] = React.useState<string | null>(null);

  /* which tab of the activity/invoices card is showing (activity · paid invoices · pending
     invoices · cancelled invoices), and the id of the open invoice details modal. */
  const [activeTab, setActiveTab] = React.useState<'activity' | 'paid' | 'pending' | 'cancelled' | 'aicredits'>(
    'activity',
  );
  const [selectedInvoiceId, setSelectedInvoiceId] = React.useState<string | null>(null);
  /* whether the "cancel this invoice?" confirmation (layered over the details modal) is open */
  const [cancelInvoiceOpen, setCancelInvoiceOpen] = React.useState(false);

  /* deposit (wallet top-up) dialog open/close */
  const [openDepositModal, setOpenDepositModal] = React.useState(false);

  /* request-payout dialog: amount to withdraw + the destination rail (one of
     bank / crypto, seeded from the saved preferred rail when opened) */
  const [openPayoutModal, setOpenPayoutModal] = React.useState(false);
  const [pendingMethod, setPendingMethod] = React.useState<PayoutMethodKind | null>(null);
  const [amountInput, setAmountInput] = React.useState('');
  // The amount field shows the raw number while focused (native caret behaviour) and the
  // grouped value when blurred, so typing never fights a re-grouping cursor jump.
  const [amountFocused, setAmountFocused] = React.useState(false);

  // The monthly settlement figures are mock (hardcodedMonthlySettlements), so they
  // load on a simulated delay; the balance + activity feed come from the real wallet
  // slice and use its own `loaded` flags. Swap this for the settlement API later.
  const settlementEp = useMockEndpoint(selectedSettlement);

  /* settlement breakdown dialog data (node Ufx4wOhvG8) — mock until the API lands */
  const breakdownEp = useMockEndpoint(mockSettlementBreakdown);
  const breakdown = breakdownEp.data;

  /* per-deduction See more / See less state, seeded from each row's Figma default */
  const [expandedDeductions, setExpandedDeductions] = React.useState<Record<string, boolean>>(() =>
    breakdown.deductions.reduce<Record<string, boolean>>((acc, d) => {
      acc[d.id] = !!d.defaultExpanded;
      return acc;
    }, {}),
  );
  const toggleDeduction = React.useCallback(
    (id: string) => setExpandedDeductions((prev) => ({ ...prev, [id]: !prev[id] })),
    [],
  );

  /* revenue-card period dropdown */
  const [revPeriodOpen, setRevPeriodOpen] = React.useState(false);

  React.useEffect(() => {
    if (!ownerWallet || !ownerWallet.loaded) {
      dispatch(fetchOwnerWallets());
    }
    if (ownerWallet && !ownerWallet.operationsLoaded && !ownerWallet.operationsLoading) {
      dispatch(fetchWalletOperations());
    }
    if (ownerWallet && !ownerWallet.transactionsLoaded && !ownerWallet.transactionsLoading) {
      dispatch(fetchWalletTransactions());
    }
    // Two calls split the withdrawal lifecycle: open requests (pending) → the Pending
    // withdrawals card; resolved requests (ne pending) → recent activity.
    if (ownerWallet && !ownerWallet.pendingWithdrawalsLoaded && !ownerWallet.pendingWithdrawalsLoading) {
      dispatch(fetchPendingWithdrawalRequests());
    }
    if (ownerWallet && !ownerWallet.resolvedWithdrawalsLoaded && !ownerWallet.resolvedWithdrawalsLoading) {
      dispatch(fetchResolvedWithdrawalRequests());
    }
    // Platform invoices back the "Invoices" tab (independent of the wallet ledger feeds).
    if (ownerWallet && !ownerWallet.invoicesLoaded && !ownerWallet.invoicesLoading) {
      dispatch(fetchInvoices());
    }
  }, [ownerWallet, dispatch]);

  /* billing prefs back the payout rails; MainLayout fetches these on auth, but
     fetch defensively in case the page is reached before that resolves */
  React.useEffect(() => {
    if (!prefsLoaded && !prefsLoading) {
      dispatch(fetchUserPreferences());
    }
  }, [prefsLoaded, prefsLoading, dispatch]);

  /* brands resolve each top-up to its casino; the layout's brand switcher usually
     loads them, but fetch defensively so the activity feed can name the casino even
     if the page is reached first */
  React.useEffect(() => {
    if (!brandsLoaded && !brandsLoading) {
      dispatch(userBrandsList());
    }
  }, [brandsLoaded, brandsLoading, dispatch]);

  /* brand lookup by casino instance id, for resolving which casino a movement funds */
  const brandsById = React.useMemo(() => new Map(brands.map((b) => [b.id, b])), [brands]);

  /* Consolidated, newest-first activity feed. Each OPERATION is one money movement; the
     ledger records it as two transactions (debit + credit) sharing an `operation_id`, so
     we show one row per operation and fold the legs away. Transactions whose operation
     isn't in the feed (e.g. a deposit) are surfaced once each, grouped by `operation_id`. */
  const allActivityRows: ActivityRow[] = React.useMemo(() => {
    const operations = ownerWallet?.operations ?? [];
    const transactions = ownerWallet?.transactions ?? [];
    const wallets = ownerWallet?.wallets ?? [];

    // wallet_id → record, so an operation-less movement can be named by the wallet
    // (type + brand) it touched rather than just its balance-after.
    const walletById = new Map(wallets.map((w) => [w.id, w]));

    // Index every transaction by its operation_id into credit/debit legs. Operation
    // rows read the destination credit leg for the post-movement balance; the
    // operation-less remainder becomes its own rows below.
    const legsByOpId = new Map<string, TxGroup>();
    for (const tx of transactions) {
      const group = legsByOpId.get(tx.operation_id) ?? {};
      if (tx.transaction_type === 'credit') group.credit = tx;
      else group.debit = tx;
      legsByOpId.set(tx.operation_id, group);
    }

    const ctx: FeedContext = { brandsById, legsByOpId, walletById };

    // Cover every operation id (any status) so a failed op's legs are never shown raw.
    const coveredOpIds = new Set(operations.map((op) => op.id));
    // A withdrawal is represented by the withdrawal-requests API (the card + resolved rows
    // below), so its MAIN ↔ WITHDRAWAL operations are dropped here to avoid a duplicate row.
    // Their ids stay in `coveredOpIds`, so the matching ledger legs are still folded away.
    // Hide the invoice-payment funding leg (MAIN → INVOICE_PAYMENTS): it duplicates the invoice record
    // and the AI-credit deposit it funds, so it never surfaces in the feed.
    const opRows = operations
      .filter((op) => op.status === 'completed' && !isWithdrawalWalletOp(op) && !isInvoicePaymentOp(op))
      .map((op) => operationToRow(t, op, ctx));

    // Surface the leftover (operation-less) transactions, one row per operation_id, but skip
    // any leg that belongs to the withdrawal lifecycle (same de-dupe reasoning as the
    // operations above). Primary signal is the leg's wallet type; as a fallback for the
    // documented drift where the WITHDRAWAL wallet is absent from /wallet/list (so the type
    // can't be resolved), the leg's description ("… withdrawal …") is the back-up signal.
    const legTouchesWithdrawal = (leg: WalletTransaction | undefined): boolean => {
      if (!leg) return false;
      if (walletById.get(leg.wallet_id)?.wallet_type === 'WITHDRAWAL') return true;
      const desc = (leg.details as { description?: unknown } | null)?.description;
      return typeof desc === 'string' && /withdrawal/i.test(desc);
    };
    // An orphan funding leg INTO the invoice-holding wallet (its CREDIT lands on INVOICE_PAYMENTS) is
    // folded away for the same reason as the operation. Keyed on the credit leg ONLY, so a real deposit
    // whose money lands elsewhere (e.g. AI_CREDITS) is never suppressed. (The funding is normally an
    // operation and already covered above; this is the belt-and-braces path.)
    const fundsInvoiceHoldingWallet = (group: TxGroup): boolean =>
      !!group.credit && walletById.get(group.credit.wallet_id)?.wallet_type === 'INVOICE_PAYMENTS';
    const orphanRows: ActivityRow[] = [];
    for (const [opId, group] of legsByOpId) {
      if (coveredOpIds.has(opId)) continue; // a leg of a known operation, folded away
      if (legTouchesWithdrawal(group.credit) || legTouchesWithdrawal(group.debit)) continue;
      if (fundsInvoiceHoldingWallet(group)) continue;
      orphanRows.push(transactionGroupToRow(t, group, ctx));
    }

    // Resolved (approved / completed / rejected / …) withdrawal requests join the feed as
    // their own rows; pending requests live in the card above, not here.
    const withdrawalRows = resolvedWithdrawals.map((req) => withdrawalRequestToRow(t, req));

    return [...opRows, ...orphanRows, ...withdrawalRows].sort((a, b) => b.ts - a.ts);
  }, [
    ownerWallet?.operations,
    ownerWallet?.transactions,
    ownerWallet?.wallets,
    resolvedWithdrawals,
    brandsById,
    t,
  ]);

  /* Split the feed: wallet money movements stay in "Wallet activity"; AI-credit movements (buys,
     usage, refunds) live in their own "AI Credits" tab, where amounts read with the Lumio mark. */
  const activityRows = React.useMemo(() => allActivityRows.filter((r) => !r.aiCredits), [allActivityRows]);
  const aiCreditRows = React.useMemo(() => allActivityRows.filter((r) => r.aiCredits), [allActivityRows]);

  /* the open row, re-resolved from the live feed by key (null when closed or gone); searches BOTH
     lists, since the details modal can open a row from either tab. */
  const selectedActivity = React.useMemo(
    () => (selectedActivityKey ? allActivityRows.find((r) => r.key === selectedActivityKey) ?? null : null),
    [selectedActivityKey, allActivityRows],
  );

  /* show the activity empty state only once both feeds have loaded with no rows,
     so the copy never flashes in during the initial fetch */
  const activityFeedsLoaded = (ownerWallet?.operationsLoaded ?? false) && (ownerWallet?.transactionsLoaded ?? false);
  const showActivityEmpty = activityFeedsLoaded && activityRows.length === 0;
  const showAiCreditsEmpty = activityFeedsLoaded && aiCreditRows.length === 0;

  /* Invoices, newest-first (the server order isn't guaranteed, so sort by issue date). The full
     list backs the details-modal lookup; the three invoice tabs show settled (Paid),
     cancelled/voided (Cancelled), and everything still open (Pending). Splitting cancelled into
     its own tab keeps voided charges from piling up in Pending. Pending is the catch-all
     remainder (not paid, not cancelled) so no invoice is silently dropped — a stray unpaid/failed
     still lands there. */
  const allInvoiceRows: InvoiceRow[] = React.useMemo(
    () => invoices.map((inv) => invoiceToRow(t, inv)).sort((a, b) => b.ts - a.ts),
    [invoices, t],
  );
  const paidInvoiceRows = React.useMemo(
    () => allInvoiceRows.filter((r) => r.invoice.status === 'paid'),
    [allInvoiceRows],
  );
  const cancelledInvoiceRows = React.useMemo(
    () => allInvoiceRows.filter((r) => isCancelledInvoice(r.invoice.status)),
    [allInvoiceRows],
  );
  const pendingInvoiceRows = React.useMemo(
    () => allInvoiceRows.filter((r) => r.invoice.status !== 'paid' && !isCancelledInvoice(r.invoice.status)),
    [allInvoiceRows],
  );
  const showPaidEmpty = invoicesLoaded && paidInvoiceRows.length === 0;
  const showPendingEmpty = invoicesLoaded && pendingInvoiceRows.length === 0;
  const showCancelledEmpty = invoicesLoaded && cancelledInvoiceRows.length === 0;

  /* the open invoice details modal, re-resolved by id from the FULL live list, so a deposit's
     linked invoice opens directly regardless of which tab (or none) is active (null when closed) */
  const selectedInvoice = React.useMemo(
    () => (selectedInvoiceId ? allInvoiceRows.find((r) => r.key === selectedInvoiceId) ?? null : null),
    [selectedInvoiceId, allInvoiceRows],
  );

  /* The cancel-confirmation only makes sense over an open invoice. If the details modal
     clears for any reason (a refetch dropping the row included), disarm the confirm so it
     can never resurface over the next invoice opened. */
  React.useEffect(() => {
    if (!selectedInvoice) setCancelInvoiceOpen(false);
  }, [selectedInvoice]);

  /* an invoice id present in the feed (so a "View invoice" link only shows when reachable) */
  const invoiceIds = React.useMemo(() => new Set(invoices.map((inv) => inv.id)), [invoices]);

  /* Open withdrawal requests for the card. The two list calls are mutually exclusive
     (eq pending vs ne pending), but a request can flip status between them, so drop any
     id that already shows up in the resolved feed, so it never appears in both places. */
  const openWithdrawals = React.useMemo(() => {
    const resolvedIds = new Set(resolvedWithdrawals.map((r) => r.id));
    return pendingWithdrawals
      .filter((r) => !resolvedIds.has(r.id))
      .slice()
      .sort((a, b) => (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0));
  }, [pendingWithdrawals, resolvedWithdrawals]);

  /* The held total is summed from the requests themselves (the source of truth), NOT the
     WITHDRAWAL wallet balance (that wallet can hold funds for requests no longer listed). */
  const withdrawalsHeldTotal = React.useMemo(
    () => openWithdrawals.reduce((sum, r) => sum + finite(Number(r.amount)), 0),
    [openWithdrawals],
  );

  // Render the card while the first fetch is in flight (skeletons) or whenever there are
  // open requests; once loaded with none, the card disappears entirely (no empty card).
  const showWithdrawalsCard = (pendingWithdrawalsLoading && !pendingWithdrawalsLoaded) || openWithdrawals.length > 0;

  /* ---------- actions (mirroring the template links) ---------- */

  /* node `0wWf2TWBEG` / `Uj287wc-Ud` onClick → onSelectSettlement (reads data-id off the pill) */
  const onSelectSettlement = React.useCallback((id: string) => {
    console.log('selecting settlement', id);

    setSelectedSettlement((prev) => hardcodedMonthlySettlements.find((s) => s.id === id) ?? prev);
  }, []);

  /* node `DR8h2je-0Z` "More details" → onOpenSettlementDialog */
  const onOpenSettlementDialog = React.useCallback(() => setOpenSettlementDialog(true), []);
  const onCloseSettlementDialog = React.useCallback(() => setOpenSettlementDialog(false), []);

  /* clicking an activity row opens its details modal */
  const onOpenActivity = React.useCallback((row: ActivityRow) => setSelectedActivityKey(row.key), []);
  const onCloseActivity = React.useCallback(() => setSelectedActivityKey(null), []);

  /* switch tabs (the count badges / panels swap) */
  const onSelectTab = React.useCallback(
    (tab: 'activity' | 'paid' | 'pending' | 'cancelled' | 'aicredits') => setActiveTab(tab),
    [],
  );

  /* clicking an invoice row opens its details modal */
  const onOpenInvoice = React.useCallback((row: InvoiceRow) => setSelectedInvoiceId(row.key), []);
  const onCloseInvoice = React.useCallback(() => {
    // While the cancel-confirmation is stacked on top, IT owns dismissal. Both dialogs
    // share the Overlay's document-level Escape listener, so without this guard a single
    // Escape would tear down the details modal too — closing both at once, and mid-request
    // slipping past the confirm's own busy lock. Suppress the lower layer so only the top
    // dialog governs closing; the effect below disarms the confirm when the invoice clears.
    if (cancelInvoiceOpen) return;
    setSelectedInvoiceId(null);
  }, [cancelInvoiceOpen]);
  const onInvoiceKeyDown = React.useCallback(
    (e: React.KeyboardEvent, row: InvoiceRow) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpenInvoice(row);
      }
    },
    [onOpenInvoice],
  );

  /* "View invoice" from a wallet-activity movement: close the activity modal and open the linked
     invoice's details modal directly, in place (no tab navigation). */
  const onViewInvoice = React.useCallback((invoiceId: string) => {
    setSelectedActivityKey(null);
    setSelectedInvoiceId(invoiceId);
  }, []);

  /* cancel an invoice (only offered for a still-open charge). The Cancel button in the
     details modal opens a confirmation; confirming calls `POST /invoice/cancel`, then
     refetches so the row leaves "Pending" and any voided charge clears from the balance. */
  const canCancelSelectedInvoice = !!selectedInvoice && isCancellableInvoice(selectedInvoice.invoice.status);
  const cancelInvoiceBusy = !!selectedInvoice && cancellingInvoiceId === selectedInvoice.invoice.id;
  const onOpenCancelInvoice = React.useCallback(() => setCancelInvoiceOpen(true), []);
  const onCloseCancelInvoice = React.useCallback(() => {
    if (cancelInvoiceBusy) return; // don't dismiss mid-request
    setCancelInvoiceOpen(false);
  }, [cancelInvoiceBusy]);
  const onConfirmCancelInvoice = React.useCallback(async () => {
    if (!selectedInvoice || cancelInvoiceBusy) return;
    try {
      await dispatch(cancelInvoice({ invoice_id: selectedInvoice.invoice.id })).unwrap();
      dispatch(fetchInvoices());
      dispatch(fetchOwnerWallets());
      setCancelInvoiceOpen(false);
      setSelectedInvoiceId(null);
      showToast(t('Invoice cancelled'), { icon: 'check' });
    } catch {
      showToast(t("Couldn't cancel the invoice. Please try again."));
    }
  }, [dispatch, selectedInvoice, cancelInvoiceBusy, showToast, t]);
  const onActivityKeyDown = React.useCallback(
    (e: React.KeyboardEvent, row: ActivityRow) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpenActivity(row);
      }
    },
    [onOpenActivity],
  );

  /* Withdrawable balance = the MAIN wallet, sourced in the SELECTED CASINO's currency (falling
     back to the primary MAIN wallet in its own currency). A budget top-up is a transfer that
     already DEBITs MAIN and CREDITs the acquisition wallet, so `main` is net of committed ad
     budgets; subtracting acquisition again would double-count money that already left MAIN.
     `resolveMainWalletForCurrency` clamps at 0 as a NaN/negative guard; balance is null until the
     wallet list loads (the headline keeps its skeleton on `ownerWallet.loaded`). */
  const casinoCurrency = useAppSelector(selectSelectedCasinoCurrency);
  const walletView = resolveMainWalletForCurrency(ownerWallet, casinoCurrency);
  const withdrawableBalance = walletView.balance ?? 0;
  // MAIN-source figures (headline balance, withdrawal amount + available) render in the resolved
  // wallet's currency, so they read in the same currency as the deposit / withdrawal being made.
  const mainMoney = (n: number): string =>
    `${currencySymbol(walletView.currency)}${formatAmountWithCode(n, walletView.currency)}`;

  /* ---------- payout rails (from billing prefs) ---------- */

  // i18n.language is a dep so the i18n.t-derived rail summaries (title/detail) re-translate
  // on a live language switch, not just when billing changes.
  const payoutMethods = React.useMemo(() => availablePayoutMethods(billing), [billing, i18n.language]);
  const hasPayoutMethod = payoutMethods.length > 0;

  /* the saved destination preference, used to pre-select a rail in the dialog */
  const preferredKind = (billing?.preferredPayoutMethod || null) as PayoutMethodKind | null;

  /* amount validation: a positive number that does not exceed the balance */
  const amountValue = Number(amountInput);
  const amountEntered = amountInput.trim() !== '';
  const amountValid =
    amountEntered && Number.isFinite(amountValue) && amountValue > 0 && amountValue <= withdrawableBalance;
  const amountError =
    amountEntered && !amountValid
      ? amountValue > withdrawableBalance
        ? t('Amount exceeds your available balance of {{amount}}', { amount: mainMoney(withdrawableBalance) })
        : t('Enter an amount greater than $0')
      : null;
  const methodPicked = !!pendingMethod && payoutMethods.some((m) => m.kind === pendingMethod);
  const canRequest = amountValid && methodPicked;

  /* open Settings → Billing to add or fix payout details (footer + in-dialog link) */
  const onEditBilling = React.useCallback(() => navigate(ROUTE.settingsSection('billing')), [navigate]);
  const onEditBillingKey = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onEditBilling();
      }
    },
    [onEditBilling],
  );

  /* deposit (wallet top-up) dialog open/close */
  const onOpenDepositModal = React.useCallback(() => setOpenDepositModal(true), []);
  const onCloseDepositModal = React.useCallback(() => setOpenDepositModal(false), []);
  /* a cleared deposit refetches everything it touches (server truth): the wallet balance, the
     activity feed (the deposit lands as a credit into MAIN), and the invoices (the deposit
     invoice flips pending → paid). The credit + paid status settle server-side via the Stripe
     webhook, which lags the in-browser confirmation by a few seconds, so refetch immediately
     (shows the pending invoice + any already-settled state) and re-poll a couple of times to
     catch the credit + paid invoice once the webhook lands, so the deposit appears without a
     manual browser refresh. */
  const onDepositSuccess = React.useCallback(() => {
    const refresh = () => {
      dispatch(fetchOwnerWallets());
      dispatch(fetchWalletOperations());
      dispatch(fetchWalletTransactions());
      dispatch(fetchInvoices());
    };
    refresh();
    window.setTimeout(refresh, 3000);
    window.setTimeout(refresh, 8000);
  }, [dispatch]);

  /* request-payout dialog open/close (seeds the destination from the saved rail) */
  const onOpenPayoutModal = React.useCallback(() => {
    setPendingMethod(resolveActivePayout(payoutMethods, preferredKind)?.kind ?? null);
    setAmountInput('');
    dispatch(clearWithdrawalSubmitError());
    setOpenPayoutModal(true);
  }, [payoutMethods, preferredKind, dispatch]);
  const onClosePayoutModal = React.useCallback(() => {
    setOpenPayoutModal(false);
    setPendingMethod(null);
    setAmountInput('');
    setAmountFocused(false);
    dispatch(clearWithdrawalSubmitError());
  }, [dispatch]);

  /* radiogroup arrow-key navigation (APG pattern): arrows move selection + focus */
  const onDestinationKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      if (!['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(e.key)) return;
      e.preventDefault();
      const delta = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1;
      const next = (index + delta + payoutMethods.length) % payoutMethods.length;
      setPendingMethod(payoutMethods[next].kind);
      (e.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
    },
    [payoutMethods],
  );

  /* amount input: keep digits + a single decimal (≤ 2 places). No caret juggling; while
     focused the field shows the raw value, so the cursor moves natively as the user types. */
  const onChangeAmount = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setAmountInput(cleanAmountInput(e.currentTarget.value));
  }, []);
  const onSetMaxAmount = React.useCallback(
    () => setAmountInput(withdrawableBalance ? cleanAmountInput(String(withdrawableBalance)) : ''),
    [withdrawableBalance],
  );
  const onAmountFocus = React.useCallback(() => setAmountFocused(true), []);
  const onAmountBlur = React.useCallback(() => setAmountFocused(false), []);

  /* Submit the payout: POST /wallet/withdrawal-request parks `amount` of the wallet's
     currency from MAIN into the WITHDRAWAL wallet and opens a `pending` request for the
     back office (we send the chosen rail as `payment_method` bank|crypto; its account
     details are resolved from billing). On success we refetch the moved money (balances), the new pending
     request, and the ledger, persist the chosen rail back into billing prefs (best-effort,
     so it pre-selects next time), and close. A server rejection (e.g. insufficient funds)
     surfaces via `withdrawalSubmitError` and keeps the dialog open. */
  const onRequestPayout = React.useCallback(async () => {
    if (!canRequest || !pendingMethod || withdrawalSubmitting) return;
    // Withdraw in the resolved MAIN wallet's currency (the selected casino's currency when a
    // matching MAIN wallet exists, else the primary MAIN wallet's own currency).
    const currency = walletView.currency;
    try {
      await dispatch(
        createWithdrawalRequest({ amount: amountValue, currency, payment_method: pendingMethod }),
      ).unwrap();
    } catch {
      return; // error rendered from withdrawalSubmitError; leave the dialog open
    }
    if (billing) {
      void dispatch(
        saveUserPreferences({
          [PREFERENCE_KEYS.billing]: pickShape({ ...billing, preferredPayoutMethod: pendingMethod }, defaultBillingPrefs()),
        }),
      );
    }
    dispatch(fetchOwnerWallets());
    dispatch(fetchPendingWithdrawalRequests());
    // Refetch the resolved list too: the back office can resolve a request right away, and
    // the card↔activity de-dupe keys off the resolved ids; a stale list would let the same
    // request show in both places.
    dispatch(fetchResolvedWithdrawalRequests());
    dispatch(fetchWalletOperations());
    dispatch(fetchWalletTransactions());
    onClosePayoutModal();
  }, [
    canRequest,
    pendingMethod,
    withdrawalSubmitting,
    walletView.currency,
    amountValue,
    billing,
    dispatch,
    onClosePayoutModal,
  ]);

  /* ---------- render helpers ---------- */

  /* nodes `0F3862fnUM` / `GS1zlWr7AE`: settlement-period dropdown */
  const renderPeriodDropdown = (isOpen: boolean, setOpen: (v: boolean) => void) => (
    <Dropdown
      className="rev-top-bar"
      isOpen={isOpen}
      //  toggle={() => setOpen(!isOpen)}
      toggle={(ev: React.PointerEvent<HTMLDivElement>) => {
        ev.preventDefault();
        ev.stopPropagation();

        const clickedId = (ev.target as HTMLElement)?.dataset?.id ?? null;

        if (clickedId) {
          onSelectSettlement(clickedId);
        }

        setOpen(!isOpen);
      }}
      direction="down"
    >
      <DropdownToggle tag="div" caret={false}>
        <div className="dd-toggle">
          <div className="rev-period-pill">
            <span className="rev-period-text">{selectedSettlement?.description ?? t('Select period')}</span>
            <BootstrapIcon
              className="rev-chevron-icon"
              styleText=""
              properties={{ iconName: 'fi-arrow-down', asSvg: true }}
              style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
          </div>
        </div>
      </DropdownToggle>
      <DropdownMenu flip end>
        <div
          className="dd-menu"
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          {hardcodedMonthlySettlements.map((s) => (
            <div
              key={s.id}
              className="rev-period-pill dd-menu"
              data-id={s.id}
              onClick={(ev: React.MouseEvent<HTMLDivElement>) => {
                ev.preventDefault();
                ev.stopPropagation();
                onSelectSettlement(s.id);
                setOpen(false);
              }}
            >
              <span className="rev-period-text" data-id={s.id}>
                {s.description}
              </span>
            </div>
          ))}
        </div>
      </DropdownMenu>
    </Dropdown>
  );

  /* node `48wZnbpJmT` → Layout: one activity row, now filled from the operations/transactions
     feed. The whole row is a button that opens the transaction-details modal. */
  const renderActivityRow = (r: ActivityRow) => {
    DEBUG && console.log('[WALLET] rendering activity row', r);
    return (
      <div key={r.key} className="activity-list-item" style={{ width: '100%' }}>
        <div className={`layout-wrp ${r.dotClass}`}>
          <div
            className="activity-row is-clickable"
            role="button"
            tabIndex={0}
            aria-label={t('View {{title}} details', { title: r.title })}
            onClick={() => onOpenActivity(r)}
            onKeyDown={(e) => onActivityKeyDown(e, r)}
          >
            <div className="activity-row-left">
              <span className="activity-icon" aria-hidden="true">
                <span className="activity-dot" />
              </span>
              <div className="activity-text-stack">
                <span className="activity-row-title">{r.title}</span>
                <span className="activity-row-sub">
                  {r.brandName ? <span className="activity-row-brand">{r.brandName}</span> : null}
                  {r.brandName ? <span className="activity-row-sep"> · </span> : null}
                  {r.sub}
                  {r.invoiceId && invoiceIds.has(r.invoiceId) ? (
                    <>
                      <span className="activity-row-sep"> · </span>
                      <span
                        className="activity-invoice-link"
                        role="link"
                        tabIndex={0}
                        aria-label={t('View linked invoice')}
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewInvoice(r.invoiceId as string);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            onViewInvoice(r.invoiceId as string);
                          }
                        }}
                      >
                        <Text text="View invoice" />
                      </span>
                    </>
                  ) : null}
                </span>
              </div>
            </div>
            <div className="activity-row-right">
              <span className={`activity-amount ${r.amountClass}${r.aiCredits ? ' is-credits' : ''}`}>
                {r.aiCredits ? <img className="credit-mark" src={LUMIO_SPARK_SRC} alt={t('AI credits')} /> : null}
                {r.amount}
              </span>
              {r.dateShort ? <span className="activity-row-date">{r.dateShort}</span> : null}
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* one invoice row in the Invoices tab — a receipt glyph, the type + number, the price and
     a status pill. The whole row opens the invoice details modal. */
  const renderInvoiceRow = (r: InvoiceRow) => (
    <div key={r.key} className="invoice-list-item" style={{ width: '100%' }}>
      <div
        className="invoice-row is-clickable"
        role="button"
        tabIndex={0}
        aria-label={t('View invoice {{number}}', { number: r.number })}
        onClick={() => onOpenInvoice(r)}
        onKeyDown={(e) => onInvoiceKeyDown(e, r)}
      >
        <div className="invoice-row-left">
          <span className="invoice-icon" aria-hidden="true">
            <InvoiceDocIcon />
          </span>
          <div className="invoice-text-stack">
            <span className="invoice-row-title">{r.title}</span>
            <span className="invoice-row-sub">
              <span className="invoice-number">#{r.number}</span>
              {r.status ? (
                <>
                  <span className="activity-row-sep">·</span>
                  <span className={`txn-status txn-status-${r.status.kind}`}>{r.status.label}</span>
                </>
              ) : null}
            </span>
          </div>
        </div>
        <div className="invoice-row-right">
          <span className="invoice-amount">{r.amount}</span>
          <span className="invoice-row-date">{formatDateShort(r.ts)}</span>
        </div>
      </div>
    </div>
  );

  /* skeleton placeholder rows while the invoice list is first loading */
  const renderInvoiceSkeletonRow = (i: number) => (
    <div className="invoice-list-item" key={`inv-skeleton-${i}`} style={{ width: '100%' }}>
      <div className="invoice-row is-skeleton">
        <div className="invoice-row-left">
          <span className="invoice-icon" aria-hidden="true">
            <InvoiceDocIcon />
          </span>
          <div className="invoice-text-stack">
            <Skeleton width="10ch" height="0.8em" radius={5} />
            <Skeleton width="14ch" height="0.7em" radius={5} />
          </div>
        </div>
        <div className="invoice-row-right">
          <Skeleton width="6ch" height="0.8em" radius={5} />
        </div>
      </div>
    </div>
  );

  console.log('[WALLET] ownerWallet state', ownerWallet);

  return (
    <PageShell className={props.className ?? 'account-wallet'} styleText={props.styleText ?? ''}>
      <div className="root">
        <div className="cnt">
          {/* ===== wallet card (node Kw7mR3xT1p) =====
              The shell is a query container: the card lays the balance and its actions out
              on one row when wide enough, and stacks the actions BELOW the balance when the
              card is too narrow (see the @container rules in wallet.scss). Querying the card's
              own width (not the viewport) keeps it correct when the docked Lumio panel or the
              side menu narrow the content area. */}
          <div className="wallet-card-shell">
            <div className="wallet-card">
              {/* header (node Qz4nL8vH2a) */}
              <div className="wallet-header">
                <Text tag="span" className="wallet-label" text="TOTAL AVAILABLE BALANCE" />
                <span className="wallet-balance">
                  {ownerWallet?.loaded ? mainMoney(withdrawableBalance) : <Skeleton width="6ch" height="0.8em" radius={6} />}
                </span>
              </div>

              {/* actions: divider + CTAs + manage-billing link. Grouped so they wrap below the
                  balance as a single unit when the card is too narrow for one row. */}
              <div className="wallet-actions">
                {/* separator (node G69GSc6uAE) */}
                <div className="separator" />

                {/* cta: Deposit (add funds via Stripe) + Request Payout (disabled until a rail is configured).
                    Widths are driven by .wallet-cta (full-width stacked on mobile, content pills on desktop). */}
                <div className="wallet-cta">
                  <Button variant="primary" icon="plus-circle" label={t('Deposit')} onClick={onOpenDepositModal} />
                  <Button
                    variant="outline"
                    icon="wallet"
                    label={t('Request Payout')}
                    disabled={!prefsLoaded || !hasPayoutMethod}
                    onClick={onOpenPayoutModal}
                  />
                </div>

                {/* footer (node Bf3tY6pN0s): manage-billing link (destination + amount are chosen in the request dialog) */}
                <div className="wallet-footer">
                  {!prefsLoaded ? (
                    <span className="footer-account">
                      <Skeleton width="12ch" height="0.8em" radius={6} />
                    </span>
                  ) : (
                    <div
                      className="footer-link-group"
                      role="button"
                      tabIndex={0}
                      onClick={onEditBilling}
                      onKeyDown={onEditBillingKey}
                    >
                      <span className="footer-link-text">
                        {hasPayoutMethod ? <Text text="Edit Billing Information" /> : <Text text="Add billing details →" />}
                      </span>
                      {hasPayoutMethod && (
                        <BootstrapIcon
                          className="footer-edit-icon"
                          styleText=""
                          properties={{ iconName: 'bi-pencil', asSvg: true }}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ===== pending withdrawals card (open payout requests awaiting the back office) ===== */}
          {showWithdrawalsCard && (
            <div className="withdrawals-card">
              <div className="withdrawals-header">
                <Text tag="span" className="withdrawals-title" text="Pending payout requests" />
                <span className="withdrawals-total">
                  {pendingWithdrawalsLoaded ? (
                    negMoney(withdrawalsHeldTotal)
                  ) : (
                    <Skeleton width="6ch" height="0.8em" radius={6} />
                  )}
                </span>
              </div>

              <div className="withdrawals-divider" />

              <div className="withdrawals-list">
                {!pendingWithdrawalsLoaded
                  ? [0, 1].map((i) => (
                      <div className="wd-row is-skeleton" key={`wd-skeleton-${i}`}>
                        <div className="wd-row-left">
                          <span className="wd-icon">
                            <BootstrapIcon styleText="" properties={{ iconName: 'bi-hourglass-split', asSvg: true }} />
                          </span>
                          <div className="wd-text-stack">
                            <Skeleton width="9ch" height="0.8em" radius={5} />
                            <Skeleton width="12ch" height="0.7em" radius={5} />
                          </div>
                        </div>
                        <div className="wd-row-right">
                          <Skeleton width="6ch" height="0.8em" radius={5} />
                        </div>
                      </div>
                    ))
                  : openWithdrawals.map((req) => {
                      const status = statusInfo(t, req.status);
                      return (
                        <div className="wd-row" key={req.id}>
                          <div className="wd-row-left">
                            <span className="wd-icon">
                              <BootstrapIcon
                                styleText=""
                                properties={{ iconName: 'bi-hourglass-split', asSvg: true }}
                              />
                            </span>
                            <div className="wd-text-stack">
                              <Text tag="span" className="wd-row-title" text="Payout" />
                              <Text
                                tag="span"
                                className="wd-row-sub"
                                text="Requested {{date}}"
                                placeholders={{ date: formatDateShort(Date.parse(req.created_at) || 0) }}
                              />
                            </div>
                          </div>
                          <div className="wd-row-right">
                            <span className="wd-amount">{negMoney(finite(Number(req.amount)))}</span>
                            {status ? (
                              <span className={`wd-status wd-status-${status.kind}`}>{status.label}</span>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
              </div>

              <Text
                tag="span"
                className="withdrawals-note"
                text="Funds are held while each payout request is reviewed."
              />
            </div>
          )}

          {/* ===== revenue card (node 5EWZD8J-wO) — hidden until the settlement API lands ===== */}
          {SHOW_MONTHLY_SETTLEMENT && (
          <div className={`revenue-card ${openSettlementDialog ? 'hide-dropdown-menu' : ''}`}>
            {/* period dropdown (node 0F3862fnUM) */}
            {renderPeriodDropdown(revPeriodOpen, setRevPeriodOpen)}

            {/* metrics row (node mz_IuScZo4) */}
            <div className="rev-metrics-row">
              <div className="rev-metric-col">
                <Text tag="span" className="rev-metric-label" text="NET REVENUE" />
                <span className="rev-metric-value rev-value-white">
                  {settlementEp.loading ? (
                    <Skeleton width="5ch" height="0.8em" radius={5} />
                  ) : (
                    money(selectedSettlement.net_revenue)
                  )}
                </span>
                <Text tag="span" className="rev-metric-helper" text="Your earnings after player winnings" />
              </div>

              <div className="separator" />

              <div className="rev-metric-col">
                <Text tag="span" className="rev-metric-label" text="PLATFORM FEE (25%)" />
                <span className="rev-metric-value rev-value-orange">
                  {settlementEp.loading ? (
                    <Skeleton width="5ch" height="0.8em" radius={5} />
                  ) : (
                    negMoney(selectedSettlement.operating_fees)
                  )}
                </span>
                <Text tag="span" className="rev-metric-helper" text="Operations & Platform" />
              </div>

              <div className="rev-metric-col">
                <Text tag="span" className="rev-metric-label" text="GAMIFICATION FEES" />
                <span className="rev-metric-value rev-value-orange">
                  {settlementEp.loading ? <Skeleton width="4ch" height="0.8em" radius={5} /> : '-$150'}
                </span>
                <Text tag="span" className="rev-metric-helper" text="Manage in Product" />
              </div>

              <div className="separator" />

              <div className="rev-metric-col">
                <Text tag="span" className="rev-metric-label" text="MONTH BALANCE" />
                <span className="rev-metric-value rev-value-green">
                  {settlementEp.loading ? <Skeleton width="5ch" height="0.8em" radius={5} /> : '$1,480'}
                </span>
              </div>
            </div>

            {/* bottom bar (node IKyZbnC1RU) */}
            <div className="rev-bottom-bar">
              <Text tag="span" className="rev-more-details" onClick={onOpenSettlementDialog} text="More details" />
            </div>
          </div>
          )}

          {/* ===== activity / invoices browser (tab strip + tabbed card) =====
              Grouped exactly like the campaign and promotion browsers: the tab strip sits on
              the PAGE and the card below it holds only the active panel. The strip keeps its
              `tablist` semantics here (it drives a real `tabpanel`, which the campaign
              browsers' `aria-pressed` toggles do not have). */}
          <TabsBar className="activity-tabs" role="tablist" aria-label={t('Wallet history')}>
            <BrowserTabScroll>
              <BrowserTab
                type="button"
                role="tab"
                id="wallet-tab-activity"
                aria-selected={activeTab === 'activity'}
                aria-controls="wallet-panel-activity"
                $active={activeTab === 'activity'}
                onClick={() => onSelectTab('activity')}
              >
                <Text text="Wallet activity" tag="span" />
                {activityFeedsLoaded ? (
                  <TabCount $bg={ACQ.badgeMutedBg} $color={ACQ.t400}>
                    <Text text={String(activityRows.length)} tag="span" />
                  </TabCount>
                ) : null}
              </BrowserTab>
              <BrowserTab
                type="button"
                role="tab"
                id="wallet-tab-paid"
                aria-selected={activeTab === 'paid'}
                aria-controls="wallet-panel-paid"
                $active={activeTab === 'paid'}
                onClick={() => onSelectTab('paid')}
              >
                <Text text="Paid invoices" tag="span" />
                {invoicesLoaded ? (
                  <TabCount $bg={ACQ.badgeMutedBg} $color={ACQ.t400}>
                    <Text text={String(paidInvoiceRows.length)} tag="span" />
                  </TabCount>
                ) : null}
              </BrowserTab>
              <BrowserTab
                type="button"
                role="tab"
                id="wallet-tab-pending"
                aria-selected={activeTab === 'pending'}
                aria-controls="wallet-panel-pending"
                $active={activeTab === 'pending'}
                onClick={() => onSelectTab('pending')}
              >
                <Text text="Pending invoices" tag="span" />
                {invoicesLoaded ? (
                  <TabCount $bg={ACQ.badgeMutedBg} $color={ACQ.t400}>
                    <Text text={String(pendingInvoiceRows.length)} tag="span" />
                  </TabCount>
                ) : null}
              </BrowserTab>
              <BrowserTab
                type="button"
                role="tab"
                id="wallet-tab-cancelled"
                aria-selected={activeTab === 'cancelled'}
                aria-controls="wallet-panel-cancelled"
                $active={activeTab === 'cancelled'}
                onClick={() => onSelectTab('cancelled')}
              >
                <Text text="Cancelled invoices" tag="span" />
                {invoicesLoaded ? (
                  <TabCount $bg={ACQ.badgeMutedBg} $color={ACQ.t400}>
                    <Text text={String(cancelledInvoiceRows.length)} tag="span" />
                  </TabCount>
                ) : null}
              </BrowserTab>
              <BrowserTab
                type="button"
                role="tab"
                id="wallet-tab-aicredits"
                aria-selected={activeTab === 'aicredits'}
                aria-controls="wallet-panel-aicredits"
                $active={activeTab === 'aicredits'}
                onClick={() => onSelectTab('aicredits')}
              >
                <Text text="AI Credits" tag="span" />
                {activityFeedsLoaded ? (
                  <TabCount $bg={ACQ.badgeMutedBg} $color={ACQ.t400}>
                    <Text text={String(aiCreditRows.length)} tag="span" />
                  </TabCount>
                ) : null}
              </BrowserTab>
            </BrowserTabScroll>
          </TabsBar>

          <div className="activity-card">
            {/* keyed by tab so the panel remounts on switch and the fade-in retriggers
                (React would otherwise reuse the div and skip the animation) */}
            <div
              className="activity-panel"
              key={activeTab}
              role="tabpanel"
              id={`wallet-panel-${activeTab}`}
              aria-labelledby={`wallet-tab-${activeTab}`}
              style={{ width: '100%' }}
            >
              {activeTab === 'activity' ? (
                showActivityEmpty ? (
                  <div className="activity-empty">
                    <span className="activity-empty-icon">
                      <BootstrapIcon styleText="" properties={{ iconName: 'bi-receipt', asSvg: true }} />
                    </span>
                    <Text tag="span" className="activity-empty-title" text="Nothing to show yet" />
                    <Text
                      tag="span"
                      className="activity-empty-body"
                      text="This is where your money movements land: monthly credit settlements, marketing and affiliate spend, and platform invoices."
                    />
                  </div>
                ) : (
                  <div className="activity-list" style={{ width: '100%' }}>
                    <div className="activity-list-items">{activityRows.map(renderActivityRow)}</div>
                  </div>
                )
              ) : activeTab === 'aicredits' ? (
                showAiCreditsEmpty ? (
                  <div className="activity-empty">
                    <span className="activity-empty-icon">
                      <img className="activity-empty-lumio" src={LUMIO_SPARK_SRC} alt="" aria-hidden="true" />
                    </span>
                    <Text tag="span" className="activity-empty-title" text="No AI credit activity yet" />
                    <Text
                      tag="span"
                      className="activity-empty-body"
                      text="Your AI credit top-ups and the credits used to generate visuals and content appear here."
                    />
                  </div>
                ) : (
                  <div className="activity-list" style={{ width: '100%' }}>
                    <div className="activity-list-items">{aiCreditRows.map(renderActivityRow)}</div>
                  </div>
                )
              ) : invoicesLoading && !invoicesLoaded ? (
                <div className="activity-list" style={{ width: '100%' }}>
                  <div className="activity-list-items">{[0, 1, 2].map(renderInvoiceSkeletonRow)}</div>
                </div>
              ) : activeTab === 'paid' ? (
                showPaidEmpty ? (
                  <div className="activity-empty">
                    <span className="activity-empty-icon">
                      <BootstrapIcon styleText="" properties={{ iconName: 'bi-receipt', asSvg: true }} />
                    </span>
                    <Text tag="span" className="activity-empty-title" text="No paid invoices yet" />
                    <Text
                      tag="span"
                      className="activity-empty-body"
                      text="Paid platform invoices and completed wallet deposits appear here."
                    />
                  </div>
                ) : (
                  <div className="activity-list" style={{ width: '100%' }}>
                    <div className="activity-list-items">{paidInvoiceRows.map(renderInvoiceRow)}</div>
                  </div>
                )
              ) : activeTab === 'cancelled' ? (
                showCancelledEmpty ? (
                  <div className="activity-empty">
                    <span className="activity-empty-icon">
                      <BootstrapIcon styleText="" properties={{ iconName: 'bi-receipt', asSvg: true }} />
                    </span>
                    <Text tag="span" className="activity-empty-title" text="No cancelled invoices" />
                    <Text
                      tag="span"
                      className="activity-empty-body"
                      text="Invoices you cancelled before payment appear here, so they stay out of Pending."
                    />
                  </div>
                ) : (
                  <div className="activity-list" style={{ width: '100%' }}>
                    <div className="activity-list-items">{cancelledInvoiceRows.map(renderInvoiceRow)}</div>
                  </div>
                )
              ) : showPendingEmpty ? (
                <div className="activity-empty">
                  <span className="activity-empty-icon">
                    <BootstrapIcon styleText="" properties={{ iconName: 'bi-receipt', asSvg: true }} />
                  </span>
                  <Text tag="span" className="activity-empty-title" text="No pending invoices" />
                  <Text
                    tag="span"
                    className="activity-empty-body"
                    text="Invoices awaiting payment appear here, including deposit top-ups still being confirmed."
                  />
                </div>
              ) : (
                <div className="activity-list" style={{ width: '100%' }}>
                  <div className="activity-list-items">{pendingInvoiceRows.map(renderInvoiceRow)}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ===== settlement breakdown dialog (node Ufx4wOhvG8) ===== */}
        <Modal
          isOpen={openSettlementDialog}
          toggle={onCloseSettlementDialog}
          fade
          backdrop
          modalDialogClassName="ows-module-wallet-dialog"
        >
          <div
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              pointerEvents: 'auto',
            }}
          >
            {/* settlement card (node 6525:55899) */}
            <div className="stlm-card">
              {/* header (node 6525:56375) */}
              <div className="stlm-header">
                <Text tag="span" className="stlm-title" text="Settlement Breakdown" />
                <div className="stlm-close-btn" onClick={onCloseSettlementDialog}>
                  <BootstrapIcon
                    className="stlm-close-icon"
                    styleText=""
                    properties={{ iconName: 'fi-close', asSvg: true }}
                  />
                </div>
              </div>

              <div className="stlm-divider" />

              {/* scrollable body */}
              <div className="stlm-body">
                {/* period pill (node 6525:56371) */}

                <div className="stlm-dropdown">
                  {renderPeriodDropdown(revPeriodOpen, setRevPeriodOpen)}
                  {/* <span className="stlm-dd-label">{breakdown.periodLabel}</span>
                  <span className="stlm-dd-caret">▾</span> */}
                </div>

                {/* REVENUE (node 6525:56294) */}
                <div className="stlm-section">
                  <Text tag="span" className="stlm-section-heading" text="REVENUE" />

                  {breakdown.revenueLines.map((line) => (
                    <div className="stlm-line-item" key={line.title}>
                      <div className="stlm-item-left">
                        <span className="stlm-item-glyph">{line.marker}</span>
                        <div className="stlm-text-stack">
                          <span className="stlm-item-title">{line.title}</span>
                          <span className="stlm-item-sub">{line.sub}</span>
                        </div>
                      </div>
                      <span className={`stlm-item-amount ${line.amount < 0 ? 'stlm-amt-orange' : 'stlm-amt-white'}`}>
                        {breakdownEp.loading ? (
                          <Skeleton width="6ch" height="0.8em" radius={5} />
                        ) : (
                          signedMoney(line.amount)
                        )}
                      </span>
                    </div>
                  ))}

                  <div className="stlm-divider" />

                  {/* gross revenue (node 6525:56311) */}
                  <div className="stlm-summary-row">
                    <div className="stlm-summary-left">
                      <Text tag="span" className="stlm-summary-label stlm-label-green" text="GROSS REVENUE" />
                      <span className="stlm-summary-sub">{breakdown.grossRevenueSub}</span>
                    </div>
                    <span className="stlm-item-amount stlm-amt-green">
                      {breakdownEp.loading ? (
                        <Skeleton width="5ch" height="0.8em" radius={5} />
                      ) : (
                        signedMoney(breakdown.grossRevenue)
                      )}
                    </span>
                  </div>
                </div>

                {/* DEDUCTIONS FROM NET REVENUE (node 6525:56316) */}
                <div className="stlm-section">
                  <Text tag="span" className="stlm-section-heading" text="DEDUCTIONS FROM NET REVENUE" />

                  {breakdown.deductions.map((d) => {
                    const expanded = expandedDeductions[d.id];
                    const hasItems = !!d.items?.length;
                    return (
                      <div className="stlm-fee-group" key={d.id}>
                        <div className="stlm-line-item">
                          <div className="stlm-item-left">
                            <span className="stlm-item-glyph">{d.marker}</span>
                            <div className="stlm-text-stack">
                              <div className="stlm-title-row">
                                <span className="stlm-item-title">{d.title}</span>
                                {hasItems && (
                                  <span className="stlm-see-link" onClick={() => toggleDeduction(d.id)}>
                                    {expanded ? <Text text="See less" /> : <Text text="See more" />}
                                  </span>
                                )}
                              </div>
                              <span className="stlm-item-sub">{d.sub}</span>
                            </div>
                          </div>
                          <span className="stlm-item-amount stlm-amt-blue">
                            {breakdownEp.loading ? (
                              <Skeleton width="5ch" height="0.8em" radius={5} />
                            ) : (
                              signedMoney(d.amount)
                            )}
                          </span>
                        </div>

                        {hasItems && expanded && (
                          <div className="stlm-expanded">
                            <div className="stlm-divider" />
                            <div className="stlm-subitems">
                              {d.items!.map((it) => (
                                <div className="stlm-subitem-row" key={it.label}>
                                  <span className="stlm-subitem-label">▪ {it.label}</span>
                                  <span className="stlm-subitem-amount">{signedMoney(it.amount)}</span>
                                </div>
                              ))}
                            </div>
                            <div className="stlm-divider" />
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* total deductions (node 6525:56363) */}
                  <div className="stlm-summary-row stlm-totals-row">
                    <Text tag="span" className="stlm-summary-label stlm-label-cyan" text="TOTAL DEDUCTIONS" />
                    <span className="stlm-item-amount stlm-amt-cyan">
                      {breakdownEp.loading ? (
                        <Skeleton width="5ch" height="0.8em" radius={5} />
                      ) : (
                        signedMoney(breakdown.totalDeductions)
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* net revenue bar (node 6525:56366) */}
              <div className="stlm-net-bar">
                <div className="stlm-net-left">
                  <span className="stlm-net-month">{breakdown.monthLabel}</span>
                  <Text tag="span" className="stlm-net-label" text="NET REVENUE" />
                </div>
                <span className="stlm-net-amount">
                  {breakdownEp.loading ? (
                    <Skeleton width="5ch" height="0.8em" radius={5} />
                  ) : (
                    signedMoney(breakdown.netRevenue)
                  )}
                </span>
              </div>
            </div>
          </div>
        </Modal>

        {/* ===== deposit dialog: Stripe top-up in the standard Overlay (bottom sheet on mobile) ===== */}
        {openDepositModal && (
          <DepositModal
            currency={walletView.currency}
            onClose={onCloseDepositModal}
            onSuccess={onDepositSuccess}
          />
        )}

        {/* ===== request payout dialog: standard Overlay (bottom sheet on mobile) ===== */}
        {openPayoutModal && (
          <Sheet
            title={t('Request payout')}
            onClose={onClosePayoutModal}
            footer={
              <div className="payout-footer">
                {withdrawalSubmitError ? (
                  <span className="payout-submit-error" role="alert">
                    {withdrawalSubmitError}
                  </span>
                ) : null}
                <Button
                  variant="primary"
                  icon="wallet"
                  label={
                    withdrawalSubmitting
                      ? t('Requesting…')
                      : amountValid
                        ? t('Request {{amount}}', { amount: mainMoney(amountValue) })
                        : t('Request Payout')
                  }
                  fullWidth
                  disabled={!canRequest || withdrawalSubmitting}
                  onClick={onRequestPayout}
                />
              </div>
            }
          >
            <div className="payout-form">
              {/* amount to withdraw, capped at the available balance */}
              <div className="payout-field">
                <div className="payout-field-head">
                  <label className="payout-field-label" htmlFor="payout-amount">
                    {t('Amount')}
                  </label>
                  <Text
                    tag="span"
                    className="payout-field-hint"
                    text="Available {{amount}}"
                    placeholders={{ amount: mainMoney(withdrawableBalance) }}
                  />
                </div>
                <div className={`payout-amount-input ${amountError ? 'has-error' : ''}`}>
                  <span className="payout-amount-prefix">{currencySymbol(walletView.currency)}</span>
                  <input
                    id="payout-amount"
                    className="payout-amount-control"
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    placeholder="0"
                    value={amountFocused ? amountInput : formatAmountDisplay(amountInput)}
                    onChange={onChangeAmount}
                    onFocus={onAmountFocus}
                    onBlur={onAmountBlur}
                    aria-invalid={!!amountError}
                    aria-describedby={amountError ? 'payout-amount-error' : undefined}
                  />
                  <button type="button" className="payout-max-btn" onClick={onSetMaxAmount} disabled={!withdrawableBalance}>
                    <Text text="Max" />
                  </button>
                </div>
                {amountError && (
                  <span id="payout-amount-error" className="payout-amount-error" role="alert">
                    {amountError}
                  </span>
                )}
              </div>

              {/* destination: one of bank / crypto */}
              <div className="payout-field">
                <Text tag="span" className="payout-field-label" text="Destination" />
                <div className="payout-options" role="radiogroup" aria-label={t('Payout destination')}>
                  {payoutMethods.map((m, idx) => {
                    const active = pendingMethod === m.kind;
                    return (
                      <button
                        type="button"
                        key={m.kind}
                        role="radio"
                        aria-checked={active}
                        tabIndex={active ? 0 : -1}
                        className={`payout-option ${active ? 'is-selected' : ''}`}
                        onClick={() => setPendingMethod(m.kind)}
                        onKeyDown={(e) => onDestinationKeyDown(e, idx)}
                      >
                        <span className="payout-option-icon">
                          <BootstrapIcon styleText="" properties={{ iconName: m.icon, asSvg: true }} />
                        </span>
                        <span className="payout-option-text">
                          <span className="payout-option-title">{m.title}</span>
                          <span className="payout-option-detail">{m.detail}</span>
                        </span>
                        <span className="payout-option-radio" aria-hidden="true" />
                      </button>
                    );
                  })}
                </div>
                <button type="button" className="payout-edit-link" onClick={onEditBilling}>
                  <Text text="Edit Billing Information →" />
                </button>
              </div>
            </div>
          </Sheet>
        )}

        {/* ===== transaction details: standard Overlay (bottom sheet on mobile) ===== */}
        {selectedActivity && (
          <Sheet title={t('Transaction details')} onClose={onCloseActivity}>
            {(() => {
              const d = selectedActivity.detail;
              return (
                <div className="txn-detail">
                  {/* hero: the amount, plus the movement status */}
                  <div className="txn-hero">
                    <span className={`txn-hero-amount ${d.amountClass}${d.aiCredits ? ' is-credits' : ''}`}>
                      {d.aiCredits ? <img className="credit-mark" src={LUMIO_SPARK_SRC} alt={t('AI credits')} /> : null}
                      {d.amount}
                    </span>
                    {d.statusLabel ? (
                      <span className={`txn-status txn-status-${d.statusKind ?? 'pending'}`}>{d.statusLabel}</span>
                    ) : null}
                  </div>
                  <span className="txn-hero-title">{d.title}</span>

                  <div className="txn-rows">
                    {buildTxnDetailRows(t, d).map((row) => {
                      // The status is shown as the hero pill above, so it's not repeated here.
                      if (row.kind === 'status') return null;
                      // The brand row carries the casino's logo / monogram next to its name.
                      if (row.kind === 'brand') {
                        return (
                          <div className="txn-row" key={row.key}>
                            <span className="txn-row-label">{row.label}</span>
                            <span className="txn-row-value txn-brand">
                              {d.brandLogo ? (
                                <img className="txn-brand-logo" src={d.brandLogo} alt="" />
                              ) : (
                                <span className="txn-brand-monogram" aria-hidden="true">
                                  {row.value.charAt(0).toUpperCase()}
                                </span>
                              )}
                              <span className="txn-brand-name">{row.value}</span>
                            </span>
                          </div>
                        );
                      }
                      return (
                        <div className="txn-row" key={row.key}>
                          <span className="txn-row-label">{row.label}</span>
                          <span
                            className={`txn-row-value ${row.mono ? 'txn-ref' : ''}`}
                            title={row.mono ? row.value : undefined}
                          >
                            {row.value}
                          </span>
                        </div>
                      );
                    })}
                    {/* a linked platform invoice, as a normal detail row whose value is a text
                        link that opens the invoice details modal in place */}
                    {d.invoiceId && invoiceIds.has(d.invoiceId) ? (
                      <div className="txn-row">
                        <Text tag="span" className="txn-row-label" text="Invoice" />
                        <button
                          type="button"
                          className="txn-row-link"
                          onClick={() => onViewInvoice(d.invoiceId as string)}
                        >
                          <Text tag="span" text="View invoice" />
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })()}
          </Sheet>
        )}

        {/* ===== invoice details: standard Overlay (bottom sheet on mobile) ===== */}
        {selectedInvoice && (
          <Sheet
            title={t('Invoice details')}
            onClose={onCloseInvoice}
            footer={
              /* Only a still-open charge can be cancelled; a paid / cancelled / failed
                 invoice is terminal, so there's no footer action for those. */
              canCancelSelectedInvoice ? (
                <button type="button" className="txn-cancel-btn" onClick={onOpenCancelInvoice}>
                  <Text text="Cancel invoice" />
                </button>
              ) : undefined
            }
          >
            <div className="txn-detail">
              <div className="txn-hero">
                <span className="txn-hero-amount amount-muted">{selectedInvoice.amount}</span>
                {selectedInvoice.status ? (
                  <span className={`txn-status txn-status-${selectedInvoice.status.kind}`}>
                    {selectedInvoice.status.label}
                  </span>
                ) : null}
              </div>
              <span className="txn-hero-title">{selectedInvoice.title}</span>

              <div className="txn-rows">
                {buildInvoiceDetailRows(t, selectedInvoice).map((row) => (
                  <div className="txn-row" key={row.key}>
                    <span className="txn-row-label">{row.label}</span>
                    <span
                      className={`txn-row-value ${row.mono ? 'txn-ref' : ''}`}
                      title={row.mono ? row.value : undefined}
                    >
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Sheet>
        )}

        {/* ===== cancel-invoice confirmation, layered over the details modal ===== */}
        {selectedInvoice ? (
          <CancelInvoiceModal
            open={cancelInvoiceOpen}
            number={selectedInvoice.number}
            amount={selectedInvoice.amount}
            busy={cancelInvoiceBusy}
            onClose={onCloseCancelInvoice}
            onConfirm={onConfirmCancelInvoice}
          />
        ) : null}

        {/* success / error confirmation pill (e.g. "Invoice cancelled") */}
        <Toast toast={toast} onDismiss={dismissToast} />
      </div>
    </PageShell>
  );
};

/** The Newton showcase shows no owner wallet: a visit to the page goes Home instead (its doors, the top bar's
 *  balance pill and the phone's More row, are hidden too). */
const WalletPage = (props: WalletProps): React.ReactElement => {
  const newtonShowcase = useAppSelector(selectNShowcase);
  return newtonShowcase ? <Navigate to={ROUTE.home()} replace /> : <Wallet {...props} />;
};

export default WalletPage;
